import {
  ActionFormSurface,
  FocusedActionSurface,
  MessageActionChips,
  useActionWsBridge,
} from "@/features/actions";
import { ActiveContextOverrideBanner, AmbientContextChips } from "@/features/active-context";
import { useCapabilityManifest, isTramaLakehouseConfigured } from "@/features/capabilities/useCapabilityManifest";
import { useProjectsStore } from "@/features/projects/state/projectsStore";
import { useAgentModes } from "@/features/agent-modes";
import { WSProvider } from "@/features/api";
import {
  CapabilityProvider,
  formatActiveConnectorsLabel,
  useActiveConnectorsCount,
  useHasCapability,
} from "@/features/capabilities";
import {
  ChatUndoChip,
  ConversationMiniRail,
  Messages,
  messageViewToLegacy,
  selectComposerDraft,
  selectCurrentConversationId,
  selectIsAnsweringFor,
  selectIsFocusMode,
  selectIsStreamingFor,
  selectMessages,
  selectQueryIdFor,
  selectReasoningLabelFor,
  selectSidebarExpanded,
  useChatLayoutStore,
  useChatStore,
  useChatWsBridge,
  useComposerSubmit,
  useConversationDetail,
  useCreateConversation,
  useDeepLinkPrompt,
  useGeneratedImageAutoOpen,
  useInterruptedQuery,
  useMessageActions,
  useQuickPrompts,
  useResolvedChatLayout,
  useSidebarStore,
  useStopQuery,
  useStreamingElapsed,
  useThinkingHops,
} from "@/features/chat";
import type { ChatMessageView } from "@/features/chat";
import {
  type SendQueryMention,
  firstNameOf,
  useAudioRecorder,
  useChatAttachments,
  useChatUserId,
  useComposerConfig,
  useDriveFolders,
  useExportConversation,
  useFeatureAccess,
  useMentionSearch,
  useScopeFolders,
} from "@/features/chat";
// Direct imports for the three symbols Rollup flagged as forming a
// circular dependency cycle through the `chat/index.ts` barrel (May 27,
// 2026 — bundling fix). Importing them from the leaf modules removes the
// cycle between `route.tsx?tsr-split=component` and the chat barrel.
import { ChatFileAudio } from "@/features/chat/components/ChatFileAudio";
import { AudioPlayer } from "@/features/chat/components/AudioPlayer";
import { ChatEmptyState } from "@/features/chat/components/ChatEmptyState";
import { CompletedActivity } from "@/features/chat/components/CompletedActivity";
import { InterruptedQueryNotice } from "@/features/chat/components/InterruptedQueryNotice";
import { ChatTopBar } from "@/features/chat/components/ChatTopBar";
import {
  ConversationCommandPalette,
  useChatCommandPalette,
} from "@/features/chat/components/ConversationCommandPalette";
import { LayoutSwitcher } from "@/features/chat/components/LayoutSwitcher";
import { SmartLayoutHint } from "@/features/chat/components/SmartLayoutHint";
import { SplitDragHandle } from "@/features/chat/components/SplitDragHandle";
import { DEFAULT_SPLIT_RATIO, clampSplitRatio } from "@/features/chat/state/chatLayoutStore";
import { useConnectSpaces, useSharedConnectSpaces } from "@/features/connect";
import { ShareLinkDialog } from "@/features/share";
import { useStructuredDataConnectors } from "@/features/connectors";
import { useEnterpriseDbBridge } from "@/features/connectors/enterprise-db-bridge";
import {
  useFilesystemAutoSync,
  useFilesystemWritebackBridge,
  useNightlyFsSync,
} from "@/features/connectors/filesystem-desktop";
import { useCapabilityConnections } from "@/features/connectors/hooks/useCapabilityConnections";
import { useDxfFiles } from "@/features/dxf";
import { KeyboardShortcutsOverlay, useGlobalShortcutsListener } from "@/features/help";
import {
  DemoBetaBanner,
  DemoBookCallBanner,
  DemoBookCallModal,
  DemoTour,
  DemoWelcomeModal,
  SectionGuide,
  useDemoTourAutostart,
} from "@/features/onboarding";
import { useProspectingWsBridge } from "@/features/sales";
import { useProfile } from "@/features/settings";
import { useSyncUserLocale } from "@/features/settings/hooks/useSyncUserLocale";
import {
  SourceConfirmationSurface,
  useSourceConfirmationWsBridge,
} from "@/features/source-confirmation";
import {
  SourceDrawerSurface,
  SourceRailSurface,
  useCitationClick,
  useSourceAutoOpen,
} from "@/features/sources";
import { useDocDiffPayloadBridge } from "@/features/sources/hooks/useDocDiffPayloadBridge";
import { useDocEditProposalBridge } from "@/features/sources/hooks/useDocEditProposalBridge";
import { useDocGenerationWsBridge } from "@/features/sources/hooks/useDocGenerationWsBridge";
import { DisplayResultChips, useStructuredDataAutoOpen } from "@/features/structured-data";
import { useStudioStore } from "@/features/studio/state/studioStore";
import { UniversalSyncToast, useSyncBatchRehydrate } from "@/features/sync";
import {
  selectActiveTab,
  selectCurrentTab,
  selectPivotHistory,
  useSyncTabFromRoute,
  useWorkspaceRouter,
  useWorkspaceStore,
} from "@/features/workspace-router";
import { WorkspaceTabs } from "@/features/workspace/components/WorkspaceTabs";
import { getWorkspaceTabDef } from "@/features/workspace/config";
import { useEagerRoutePrefetch } from "@/features/workspace/hooks/useEagerRoutePrefetch";
import { asDriveProvider } from "@ando/core";
import { searchMentions } from "@ando/core/api";
import { getSession, hydrateSession, isPlatformAdmin } from "@ando/core/auth";

// Deep import (NOT the console barrel) — the barrel re-exports every console
// page and would drag the whole console feature into the product shell chunk.
import { surfaceAccess } from "@/features/console/access";
import type { ChatFileAttachment, ScopedDocument } from "@ando/core/domain";
import type { MessageView } from "@ando/core/domain";
import type { MentionAutocompleteResult, ScopeDocumentItem, ScopeFolderItem } from "@ando/ui";
import {
  BrandBlobs,
  type CadFeatureFilterResult,
  type CadModalFile,
  type CadSpatialFilterResult,
  Composer,
  type ComposerAttachKind,
  type ComposerChip,
  type ComposerMode,
  type ConnectAreaOption,
  type FileChip,
  StreamingIndicator,
  ThinkingChain,
  TooltipProvider,
  cn,
} from "@ando/ui";
import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Outlet, createFileRoute, redirect, useLocation, useMatches, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import {
  Blend,
  Crosshair,
  Download,
  FileText,
  FlaskConical,
  Folder,
  Globe,
  HelpCircle,
  Maximize,
  Maximize2,
  Minimize2,
  Trash2,
  X,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * `_authed` route group — the authenticated split-screen shell.
 *
 * Layout (v18 mockup, May 23, 2026 chrome-relocation refresh):
 *   - **Left mini-rail (56px)**: ProjectPill (compact) · ChatNav (3) ·
 *     NotificationBell · StudioPill (compact) · UserMenu (avatar).
 *     ``ConversationMiniRail`` owns the full top-bar — there is no
 *     absolute-positioned top chrome anymore (gained ~56px of vertical
 *     space on every surface).
 *   - **Left 38% (minus rail)**: Chat pane (Messages + Composer)
 *   - **Right 62%**: Workspace pane (top tabs + sub-tab bar + Outlet surface).
 *     Canvas + Dashboard are first-class tabs (May 26, 2026 — replaces
 *     the previous floating OutputRail).
 *
 * Auth guard: `beforeLoad` reads the user-info cache via `@ando/core/auth`;
 * missing session → throw `redirect({ to: '/login' })` which TanStack Router
 * catches before mount (no flash of protected content). The BFF still owns
 * the authoritative `ando_session` httpOnly cookie.
 */
export const Route = createFileRoute("/_authed")({
  beforeLoad: async ({ location }) => {
    // ``hydrateSession`` (not ``getSession``): on desktop the tokens live in
    // the OS keychain behind an async invoke — the guard must await the
    // one-shot hydrate or a cold start / fresh IdP login bounces to /login.
    const session = await hydrateSession();
    if (!session) {
      throw redirect({
        to: "/login",
        search: location.pathname.startsWith("/engine")
          ? { intent: "engine" }
          : undefined,
      });
    }
    // DUAL-SURFACE inverse guard (Jul 2026): a console-only session
    // (``platform-dev`` — no product entitlement) must never land on a
    // product route. EVERY authed route funnels through this ``beforeLoad``
    // (``/console`` included — it lives under ``_authed/_modal``), so the
    // path check exempts the console subtree and everything else bounces
    // to ``/console``. Single choke point — no per-route edits needed; the
    // console's own gate stays in ``_modal/console/route.tsx``.
    if (
      !surfaceAccess(session).product &&
      !location.pathname.startsWith("/console")
    ) {
      throw redirect({ to: "/console" });
    }
    // NOTE: do NOT re-route on ``?status=connected&provider=...`` here.
    // ``/integrations`` lives UNDER ``_authed``, so redirecting to it from this
    // guard (while re-emitting the same params) made ``beforeLoad`` fire again on
    // its own target → infinite redirect loop → router aborts with no committed
    // match → BLANK page after Google Drive OAuth (June 2026). The flow is already
    // handled end-to-end: the BE redirects to ``/auth/callback`` (not the root),
    // ``/auth/callback`` routes to ``/integrations`` with the params, and the
    // integrations index ``useEffect`` consumes + strips them and fires the toast.
    // The legacy root ``?page=...&status=connected`` flow this block guarded is
    // dead. (Was: May 24, 2026 OAuth callback re-route.)
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  // Console-only users must not mount the product runtime. Those bridges
  // assume a product workspace (chat state, local filesystem/Tauri APIs,
  // connector manifests and workspace routes); mounting them behind the
  // full-screen Console briefly paints the Console and then can crash the
  // entire React tree as their initial effects resolve. Keep the entitlement
  // split structural so hooks are never called conditionally.
  if (!surfaceAccess(getSession()).product) {
    return (
      <TooltipProvider delayDuration={400} skipDelayDuration={150}>
        <Outlet />
      </TooltipProvider>
    );
  }

  return <ProductAuthedLayout />;
}

/** Product runtime providers and eager background work. */
function ProductAuthedLayout() {
  // Workspace preferences live behind its entitlement. Console-only users
  // use the locale selected at boot and must not fetch /users/{id} (403).
  useSyncUserLocale();

  // Warm the hottest tabs (Conversations / Documents / Calendar) in idle time
  // so their FIRST open paints from cache, not just on hover-intent.
  useEagerRoutePrefetch();

  // ``CapabilityProvider`` sits inside ``WSProvider`` so it can subscribe to
  // the typed ``capabilities_changed`` WS event for cache invalidation.
  //
  // ``TooltipProvider`` (Radix) MUST live ONCE at the app root so the
  // ``delayDuration`` coordination works across the entire tree —
  // multiple providers cause tooltip flicker and break the global
  // skip-delay window. Every Radix Tooltip in the descendant tree
  // (``SourceRail`` chips, future MessageCitation tooltips, etc.)
  // reads from this single provider.
  return (
    <TooltipProvider delayDuration={400} skipDelayDuration={150}>
      <WSProvider>
        <CapabilityProvider>
          <AuthedShell />
        </CapabilityProvider>
      </WSProvider>
    </TooltipProvider>
  );
}

/**
 * Inner shell — kept separate so the WS provider sits OUTSIDE the
 * workspace state subscriptions. ``useWorkspaceStore`` runs here, where
 * the WSProvider is already mounted; descendants can call ``useWsStatus()``
 * / ``useWsClient()`` without an extra wrapper.
 */
function AuthedShell() {
  // Lingui resolver — wraps every static IT label in the workspace shell.
  const { i18n } = useLingui();
  // Mount the WS → chat store bridge — subscribes 24 chat-event types so
  // every BE frame lands in the right slice. Disposer fires on logout.
  useChatWsBridge();

  // First-run demo storyboard — auto-starts once per browser for demo users.
  useDemoTourAutostart();

  // Mount the batch sync-status rehydrate (May 25, 2026). One call to
  // ``GET /sync-status/all`` on cold-boot + WS reconnect + window focus
  // seeds every active sync job into the ``SyncStore``, so the
  // ``UniversalSyncToast`` lights up instantly without per-feature
  // polling. Cheap (30s background poll) and idempotent.
  useSyncBatchRehydrate();

  // Mount the route → store sync (June 10, 2026). Treats the TanStack
  // location as the single source of truth for ``WorkspaceTabs``
  // active state — kills the drift where the top bar highlighted the
  // wrong tab after deep-links, back/forward, or the smart redirect
  // in ``_authed/index.tsx``. See ``useSyncTabFromRoute`` for the
  // entry-point breakdown.
  useSyncTabFromRoute();

  // Mount the desktop filesystem live auto-sync — (re-)establishes the native
  // Rust watchers for the user's filesystem connectors on every authed mount
  // (watches don't survive a Tauri restart, so this IS "resume on launch") and
  // drives the shared incremental sync on each watcher batch. Self-gates on
  // isTauri, so it's a no-op on web.
  useFilesystemAutoSync();

  // Nightly fs-sync — listens for the Rust scheduler's 24h tick
  // (``fs-nightly-sync-tick``) and re-runs ``runFilesystemSync`` for every
  // active filesystem connector. Pairs with the BE
  // ``spreadsheet-sync-dispatch`` schedule (05:00 UTC, 2h after the
  // Tauri tick) so freshly-uploaded xlsx files get their DB tables
  // re-imported in the same 24h window. Self-gates on ``isTauri``.
  useNightlyFsSync();

  // Mount the BE → desktop filesystem writeback bridge — listens to
  // ``filesystem_write_back`` WS frames, writes the bytes to the local/NAS
  // path via the Tauri command ``fs_write_file_bytes``, and ACKs back so
  // the BE coroutine in ``FilesystemConnector.update_file`` unblocks. Self-
  // gates on ``isFilesystemConnectorAvailable`` → no-op on web (June 2026).
  useFilesystemWritebackBridge();

  // Mount the BE → desktop on-prem DB bridge — listens to
  // ``enterprise_db_query`` WS frames, resolves the DSN from the OS keychain (Rust `keyring`)
  // by handle, executes the SQL locally via the Tauri command
  // ``db_execute_sql``, and POSTs the result back so the BE coroutine in
  // ``WSRelayDatabaseConnector._rpc`` unblocks. Self-gates on
  // ``isEnterpriseDbBridgeAvailable`` → no-op on web.
  useEnterpriseDbBridge();

  // Mount the WS → action router bridge — same WSClient singleton, listens
  // to ``action_proposal`` + ``action_status`` and fans them into the
  // ``activeActionStore`` + auto-navigates the workspace tab. Without
  // this, BE ``propose_document_fork_edit`` proposals would land in
  // Redis but never surface in the UI (May 22, 2026 incident).
  useActionWsBridge();

  // Mount the WS → source-confirmation router bridge — listens to
  // ``action_source_confirmation`` (May 22, 2026 "Acme but got Brini"
  // follow-up). Pumps the ``sourceConfirmationStore`` and routes the
  // user to the Documenti tab so the disambiguation card lands in
  // front of them before the eventual action_proposal fires.
  useSourceConfirmationWsBridge();

  // Mount the WS → doc-generation bridge — listens to ``doc_generation_started``
  // (Jun 22, 2026). Shows the work-area "Genero…" loader during the ~40s
  // authoring window of a no-confirmation document create/fork/edit, so the
  // unique-source path is never the dead-looking limbo the source-confirmation
  // path already avoids.
  useDocGenerationWsBridge();

  // Mount the WS → prospecting bridge — listens to ``prospecting_run_done``
  // (durable Sales discovery / deep-research / bulk-send / backfill finished),
  // clears the in-flight loader for that campaign and toasts the outcome so the
  // leads view never has to guess via a timeout (June 2026).
  useProspectingWsBridge();

  // Mount the global keyboard-shortcuts listener — owns ⌘/, ?, and Esc
  // backup for the help overlay. Mounted ONCE at the authed shell level
  // (re-mounting deeper would register duplicate listeners).
  useGlobalShortcutsListener();

  // ── Conversation sidebar hybrid (mini-rail 48px ↔ panel 260px) ──────
  //
  // Single owner of the open state lives in `useSidebarStore` (persisted).
  // The `useChatCommandPalette` hook owns the Cmd+K global binding; we
  // mount the palette overlay once at the shell level so it overlays the
  // entire authed surface regardless of which tab is active.
  const sidebarExpanded = useSidebarStore(selectSidebarExpanded);
  const toggleSidebar = useSidebarStore((s) => s.toggleExpanded);
  const setSidebarExpanded = useSidebarStore((s) => s.setExpanded);
  const { create: createConversation } = useCreateConversation();
  const selectConversation = useChatStore((s) => s.selectConversation);
  const { open: paletteOpen, setOpen: setPaletteOpen } = useChatCommandPalette();

  // ``OutputRail`` removed May 26, 2026 — Canvas + Dashboard are now
  // first-class working-bar tabs (see ``features/workspace/config.ts``).
  // The previous floating right-edge rail was deleted because it
  // duplicated Canvas chrome and hid Dashboard behind an icon-only
  // secondary nav that the user reported as hard to discover.

  const onNewChatFromRail = (): void => {
    const id = createConversation();
    selectConversation(id);
    // New chat is empty by definition; closing the overlay surfaces the
    // composer immediately. No-op when the sidebar is already collapsed.
    setSidebarExpanded(false);
  };

  // ── Adaptive layout (May 30, 2026 — 3-mode) ───────────────────────────
  //
  // Linear / Notion / Cursor pattern. Three modes: ``split`` (38/62),
  // ``canvas-led`` (chat collapsed to 48px rail, workspace ~full), and
  // ``focus`` (chat 760px centred, workspace hidden). Per-tab override
  // persisted, with surface-aware defaults: Dashboard + Canvas open in
  // canvas-led automatically; chat-led tabs (Conversazioni, Calendario,
  // …) open in split. See ``chatLayoutStore.ts`` for the resolution rule.
  //
  // Cmd/Ctrl + "." → cycle modes for the current tab
  // Cmd/Ctrl + "\\" → toggle focus on/off for the current tab
  // ESC → exit focus mode (when no modal is open)
  const currentTab = useWorkspaceStore(selectCurrentTab);
  const {
    isFocus: isFocusMode,
    isCanvasLed,
    isSplit: isSplitMode,
  } = useResolvedChatLayout(currentTab);
  const cycleForTab = useChatLayoutStore((s) => s.cycleForTab);
  const toggleFocusForTab = useChatLayoutStore((s) => s.toggleFocusForTab);
  const setModeForTab = useChatLayoutStore((s) => s.setModeForTab);
  // Per-tab chat width % (split mode only). ``splitRatioByTab`` is the
  // user-overridden value; we fall back to ``DEFAULT_SPLIT_RATIO`` (38%)
  // when the user hasn't touched the divider yet for this tab.
  // ``clampSplitRatio`` re-bounds any stale persisted value from an
  // earlier bounds policy (was 20/70, now 38/62) so users don't see
  // the chat sitting outside the allowed range until the next drag.
  const splitRatio = clampSplitRatio(
    useChatLayoutStore((s) => s.splitRatioByTab[currentTab]) ?? DEFAULT_SPLIT_RATIO,
  );
  // Container ref for the SplitDragHandle's pixel→percentage math.
  const shellRowRef = useRef<HTMLDivElement | null>(null);

  // ── Modal-route detection (June 15, 2026) ──────────────────────────
  //
  // Routes under ``_authed/_modal/**`` (Settings, Brand Kits, Org admin,
  // Integrations, …) render a full-screen ``<ModalShell>`` that owns
  // the viewport — there is NO chat/workspace split underneath. The
  // split chrome (SplitDragHandle, SmartLayoutHint, LayoutSwitcher) is
  // semantically dead in those routes AND visually bleeds through the
  // modal layer (the orange divider line is z-20 in this shell, the
  // modal is z-40 — `position: fixed` should cover it, but the dragable
  // hit-strip + native tooltip create perceived chrome the user has no
  // way to dismiss). Skip the split chrome entirely when a modal is
  // active so the takeover layout reads as a true single surface.
  //
  // ``useMatchRoute({ to: "/_authed/_modal" })`` would be cleaner but
  // TanStack Router's typed route table only allows LEAF route paths
  // as ``to:`` values — layout groups (``_modal``) aren't selectable.
  // ``useMatches`` walks the actual matched chain and lets us check
  // by route id, which IS the canonical way to detect a layout group.
  const matches = useMatches();
  const isModalRoute = matches.some((m) => m.routeId.startsWith("/_authed/_modal"));

  // Global keyboard: ⌘. cycles, ⌘\ toggles focus, ESC exits focus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      // Cmd/Ctrl + "." (period) cycles modes for the current tab.
      if ((e.metaKey || e.ctrlKey) && e.key === ".") {
        e.preventDefault();
        cycleForTab(currentTab);
        return;
      }
      // Cmd/Ctrl + "\\" (backslash) toggles focus on/off for the current tab.
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") {
        e.preventDefault();
        toggleFocusForTab(currentTab);
        return;
      }
      // ESC exits focus mode if no other modal is open.
      if (e.key === "Escape" && isFocusMode) {
        if (document.querySelector('[role="dialog"][data-state="open"]')) return;
        // Drop the focus override → resolves back to tab default
        // (canvas-led on Dashboard/Canvas, split elsewhere).
        setModeForTab(currentTab, isCanvasLed ? "canvas-led" : "split");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentTab, cycleForTab, toggleFocusForTab, setModeForTab, isFocusMode, isCanvasLed]);

  // Row-click handler used by the overlay sidebar — select + auto-close so
  // the user sees the loaded conversation without a second click on the
  // backdrop. The non-overlay (legacy panel) mount path uses the store
  // mutation directly.
  const onSidebarSelect = (id: string): void => {
    selectConversation(id);
    setSidebarExpanded(false);
  };

  // Per-message toolbar handler (copy / edit / retry / pin / branch).
  // Wiring lives in ``useMessageActions`` so the route stays free of
  // side-effect plumbing. The Messages component invokes ``onAction``
  // with the (action, message) tuple; the hook routes each action to
  // the right slice (clipboard / composer draft / send-query / pin
  // toggle / branch toast).
  const { onAction: onMessageToolbarAction } = useMessageActions();

  // Citation handler: routes the workspace + opens the SourceDrawer for the
  // clicked citation. `useCitationClick` owns the cache + tab-pulse +
  // drawer-open pipeline; <SourceDrawerSurface> reads the resulting store
  // state. See `features/sources/hooks/useCitationClick.ts`.
  const { onCitationClick } = useCitationClick();

  // Auto-open the top-ranked source the moment an agent answer lands —
  // polymorphic: document → PDF viewer, email → email pane, db → row view,
  // calendar → event card, etc. Reuses the SAME pipeline as click-on-chip
  // so the drawer-open semantics stay identical. Manual chip clicks still
  // work in parallel to swap to alternative sources.
  // See `features/sources/hooks/useSourceAutoOpen.ts`.
  const autoOpenWorkspaceResults = useLocation({ select: (location) => location.pathname.startsWith("/workspace/") });
  useSourceAutoOpen(autoOpenWorkspaceResults);

  // AI HITL bridge — subscribe to ``doc_edit_proposal`` WS frames and fan
  // them into ``useProposalsStore``. The active ``<ArtifactPane>`` reads
  // its own ``doc_id`` slice and applies suggestions via the editor's
  // imperative ``applyAIProposal()`` handle. Mount once here.
  useDocEditProposalBridge();

  // Phase 2.1 cross-mime diff envelope — subscribe to ``doc_diff_payload``
  // WS frames and fan them into ``useDiffStore``. Consumed by the new
  // ``<ReviewRail>`` (DRY companion to ``AISummaryPanel``). Mount once.
  useDocDiffPayloadBridge();

  // May 30, 2026 — V1 parity for the chat composer's "Dati strutturati"
  // toggle. Watches the current conversation for incoming structured
  // results AND ``config.focus !== "standard"`` → auto-navigates to
  // ``/workspace/dashboard?openResult=<msgId>`` so the user sees the
  // dedicated panel instead of just an inline card. Single subscription
  // (dedup per message id), no double-fires across re-renders / nav.
  useStructuredDataAutoOpen(autoOpenWorkspaceResults);
  // Same pattern for AI-generated images, but ALWAYS opens (no focus gate):
  // the working area shows the freshly-created image (Documenti ?focus=docId).
  // The image itself stays inline in the chat; a thin sotto-chat chip (see
  // ``Messages``) re-opens it from history / other sections.
  useGeneratedImageAutoOpen(autoOpenWorkspaceResults);

  const storeMessages = useChatStore(selectMessages);
  const currentConversationId = useChatStore(selectCurrentConversationId);

  // Hydrate the per-conversation message list whenever the user switches
  // entries from the sidebar. TanStack Query caches per
  // (user, conversation_id) so re-mounting with the same id never refetches
  // unless the cache is stale (matches the v1
  // `ConversationContext.retrieveConversation` semantics). Without this
  // wire-up, `selectConversation` flipped the current id but the BE
  // `GET /conversations/history/:user/:conv` was never invoked and the
  // viewport stayed empty.
  useConversationDetail(currentConversationId);

  // ESC closes the expanded sidebar — global keydown listener mounted only
  // while the overlay is visible to avoid leaking handlers across renders.
  useEffect(() => {
    if (!sidebarExpanded) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setSidebarExpanded(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [sidebarExpanded, setSidebarExpanded]);

  // The expanded conversation-sidebar is an OVERLAY sized for SPLIT mode
  // (the 38% chat pane is wide enough to hold its 384px panel + backdrop).
  // The moment the workspace goes full-width — canvas-led (chat is a 48px
  // rail), focus mode, or a modal takeover route — that panel spills OVER
  // the surface and its backdrop (``left-14 right-0``) collapses, so the
  // surface shows THROUGH it (user report: "va sopra e trasparente").
  // Collapse it whenever we're not in split, so a full-screen surface is
  // always clean and opaque.
  useEffect(() => {
    if (!isSplitMode || isModalRoute) setSidebarExpanded(false);
  }, [isSplitMode, isModalRoute, setSidebarExpanded]);

  // Any navigation collapses the sidebar overlay (Notion / Linear pattern):
  // opening a full-screen surface — a Dashboard ``?openResult=`` result panel,
  // a Settings takeover, a new tab — always changes the location, so this
  // one effect covers every "went full-screen, sidebar still bleeding" case.
  // ``searchStr`` is included so a same-path query change (openResult) counts.
  const locationKey = useLocation({
    select: (l) => `${l.pathname}${l.searchStr}`,
  });
  useEffect(() => {
    setSidebarExpanded(false);
  }, [locationKey, setSidebarExpanded]);

  const isStreaming = useChatStore(selectIsStreamingFor(currentConversationId));
  const reasoningLabel = useChatStore(selectReasoningLabelFor(currentConversationId));
  const cancellationLabel = useChatStore((s) => currentConversationId ? s.streamingByConversation[currentConversationId]?.cancellationLabel : undefined);
  const activeQueryId = useChatStore(selectQueryIdFor(currentConversationId));
  const isAnswering = useChatStore(selectIsAnsweringFor(currentConversationId));
  const streamingElapsed = useStreamingElapsed(isStreaming);
  const stopQuery = useStopQuery();
  const { interrupted, retry: retryInterrupted, clear: clearInterrupted } = useInterruptedQuery();
  // Jul 3, 2026 — SOTA extended-thinking streaming. The hook now also
  // exposes the sanitised thinking buffer + duration so ``<ThinkingChain>``
  // can render the live block AND collapse to a chip after the answer
  // arrives (Claude iOS / Cursor Composer pattern).
  const {
    hops: thinkingHops,
    streamingThinking,
    thinkingFinalized,
    thinkingDurationMs,
  } = useThinkingHops(activeQueryId, isAnswering);
  const pivotHistory = useWorkspaceStore(selectPivotHistory);
  const { revertLastPivot } = useWorkspaceRouter();
  const lastPivot = pivotHistory.at(-1) ?? null;

  // Adapter — `<Messages>` still consumes the legacy view-model.
  const messages: ChatMessageView[] = storeMessages.map(messageViewToLegacy);

  // Streaming affordance hierarchy:
  //   - hops present → rich `<ThinkingChain>` card (header + hop list +
  //     elapsed timer + Interrompi + expandable details)
  //   - no hops yet → 3-tier `<StreamingIndicator>` (hidden → dots → blob)
  let streamingSlot: ReactNode = null;
  if (isStreaming) {
    // Show the rich card when we have EITHER a hop scaffold OR a
    // thinking stream OR we've already flipped to isAnswering — the
    // thinking stream can precede the first reasoning_hop by hundreds
    // of ms and the card is the correct surface for that early signal.
    if (thinkingHops.length > 0 || isAnswering || streamingThinking) {
      streamingSlot = (
        <ThinkingChain
          hops={thinkingHops}
          isAnswering={isAnswering}
          statusLabel={cancellationLabel}
          elapsedMs={streamingElapsed}
          onStop={stopQuery}
          streamingThinking={streamingThinking}
          thinkingFinalized={thinkingFinalized}
          thinkingDurationMs={thinkingDurationMs}
        />
      );
    } else {
      streamingSlot = (
        <StreamingIndicator elapsedMs={streamingElapsed} reasoningHopLabel={reasoningLabel} />
      );
    }
  }
  const interruptedHere =
    !isStreaming && interrupted?.conversation_id === currentConversationId ? interrupted : null;
  if (interruptedHere) {
    streamingSlot = (
      <InterruptedQueryNotice
        key={interruptedHere.query_id}
        onRetry={retryInterrupted}
        onDismiss={clearInterrupted}
      />
    );
  }

  return (
    // Column wrapper: the slim demo BETA banner (demo-only) reserves its own
    // height ABOVE the split-screen shell row so nothing is hidden behind it.
    // For non-demo sessions ``<DemoBetaBanner>`` renders null → the column has
    // a single ``flex-1`` child and the layout is byte-identical to before.
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-transparent font-sans text-ink-1">
      {/* ── Slim, always-visible BETA notice (demo-only, flow element) ── */}
      <DemoBetaBanner />

      {/* ── Soft "book a call" nudge (demo-only, trigger-gated) ── */}
      <DemoBookCallBanner />

      {/* Root is transparent so the BrandBlobs (fixed, z-0) show through.
          The body still has a solid cream (`hsl(var(--background))` in
          packages/ui/src/styles.css) as the safe fallback colour. */}
      <div
        ref={shellRowRef}
        className="relative flex min-h-0 w-full flex-1 overflow-hidden"
        data-chat-mode={isFocusMode ? "focus" : isCanvasLed ? "canvas-led" : "split"}
      >
        {/* ── Ambient brand background blobs (fixed z-0, pointer-none) ── */}
        <BrandBlobs />

        {/* ── First-run demo BETA welcome modal + storyboard (portals;
            no-op unless it's a demo session / the tour is active) ── */}
        <DemoWelcomeModal />
        <DemoBookCallModal />
        <DemoTour />

        {/* ── Layout switcher (top-right overlay) ───────────────────────
          Discoverable affordance for the 3-mode chat/workspace layout
          (Split / Workspace-led / Focus). Always visible — replaces the
          previously hidden Cmd+./Cmd+\\ shortcuts which users didn't
          know existed. May 31, 2026 — SOTA contextual layout step 1.

          Suppressed on ``_authed/_modal/**`` routes: those takeover
          layouts have no split to switch (June 15, 2026). */}
        {isModalRoute ? null : (
          <div className="pointer-events-none absolute top-3 right-4 z-30 flex items-center gap-2">
            <div className="pointer-events-auto">
              <SmartLayoutHint />
            </div>
            {/* The switcher normally lives INSIDE the WorkspaceTabs bar (rightSlot).
              In focus mode that bar is hidden, so surface it here so the user can
              always leave full-screen. */}
            {isFocusMode ? (
              <div className="pointer-events-auto">
                <LayoutSwitcher />
              </div>
            ) : null}
          </div>
        )}

        {/* NOTE (May 23, 2026): the top-bar cluster (project pill + notif +
          studio + avatar) was relocated INTO ``ConversationMiniRail`` so
          we could drop the ``pt-16`` reservation on both columns and
          gain ~56px of usable vertical space everywhere (chat,
          documents, calendar, inbox, settings…). The mini-rail now
          hosts the full chrome — see ConversationMiniRail.tsx. */}

        {/* ── Left 38%: persistent mini-rail + overlay sidebar + Chat pane ─
          The mini-rail (48px) is ALWAYS visible on the left edge so the
          three rail actions (expand / search / new chat) stay one click
          away. The expanded sidebar floats as an OVERLAY (absolute, z-30)
          on top of the messages — instead of shrinking them — with a
          backdrop that closes the overlay on click. ESC also closes (see
          the global keydown effect above). */}
        {/* Modal takeover routes (Settings / Integrations / Console / Admin)
          render ONLY the Outlet — which portals the full-screen shell to
          <body>. The workspace shell below must NOT mount behind it: its
          surfaces are transparent by design (``SurfaceLayout`` relies on the
          workspace pane's own translucent bg), so a mounted workspace bleeds
          the chat + tabs through the takeover. Nothing behind ⇒ nothing to
          bleed, independent of any stacking/portal subtlety. */}
        {isModalRoute ? <Outlet /> : null}
        {isModalRoute ? null : (
        <>
        <section
          className={cn(
            "relative flex h-full flex-row bg-cream/40 transition-[width] duration-200 ease-out",
            // When the sidebar overlay is open it must sit ABOVE the workspace
            // pane (also z-10, later in the DOM so it wins by default) — else
            // in canvas-led/focus the full-width workspace renders THROUGH the
            // overlay (the "trasparenze/alpha" bleed). Lift the whole chat pane
            // so the overlay + its backdrop cover the workspace; drop back to
            // z-10 when collapsed so the split divider stacks normally.
            sidebarExpanded ? "z-40" : "z-10",
            // 3-mode layout (May 31, 2026):
            //   - focus      : chat takes the FULL viewport width, workspace
            //                  hidden. The composer + messages still cap
            //                  themselves to a readable measure inside, but
            //                  the chrome owns the screen.
            //   - canvas-led : chat collapsed to the 48px mini-rail; the
            //                  expanded chat content is hidden via the inner
            //                  ``hidden`` class below so the workspace gets
            //                  the full canvas. Default for Dashboard/Canvas.
            //   - split      : chat width state-driven (splitRatioByTab),
            //                  default 38%, drag handle to the right.
            //                  No border-r here — the SplitDragHandle below
            //                  owns the visible divider.
            isFocusMode
              ? "w-full"
              : isCanvasLed
                ? // Drop ``overflow-hidden`` while the sidebar is expanded so the
                  // left-0 overlay can extend past the 48px rail instead of being
                  // clipped to it.
                  cn("w-[48px] border-hairline border-r", !sidebarExpanded && "overflow-hidden")
                : null,
          )}
          // Inline width applies only in split mode — focus/canvas-led use
          // the Tailwind utilities above.
          style={isSplitMode ? { width: `${splitRatio}%`, transition: "none" } : undefined}
          aria-label={i18n._(msg`Pannello conversazione Ando`)}
        >
          {/* ── Collapsible sidebar (Jul 2026 — ONE bar, shadcn pattern) ──────
              Always mounted, absolute OVERLAY (never steals workspace width),
              CSS ``transition-[width]`` between 56px (icons only) and 384px
              (icons + labels + conversation list). Each row is ``[icon][label]``
              with the icon at a fixed inset, so the icon never moves — only the
              text reveals as the clip widens. The chat keeps a ``ml-14`` gutter
              for the collapsed bar. */}
          {sidebarExpanded ? (
            <button
              type="button"
              onClick={() => setSidebarExpanded(false)}
              aria-label={i18n._(msg`Chiudi sidebar`)}
              // ``fixed inset-0`` dims the WHOLE viewport behind the sidebar.
              // The old ``absolute … left-14 right-0`` collapsed to nothing
              // when the chat pane is the 48px canvas-led rail (left-14 > the
              // pane's right edge), so the workspace showed through the open
              // sidebar. The opaque ``<aside>`` (z-40) renders over this for
              // its 384px, so the net effect is: sidebar solid, rest dimmed.
              className="fixed inset-0 z-30 cursor-default bg-black/30 duration-200 animate-in fade-in"
            />
          ) : null}
          <aside
            aria-label={i18n._(msg`Cronologia conversazioni`)}
            className={cn(
              "absolute inset-y-0 left-0 z-40 overflow-hidden border-hairline border-r bg-cream transition-[width] duration-200 ease-[cubic-bezier(0.2,0.7,0.2,1)]",
              sidebarExpanded ? "w-96 shadow-[4px_0_16px_-8px_rgba(60,40,20,0.18)]" : "w-14",
            )}
          >
            <ConversationMiniRail
              onExpand={toggleSidebar}
              onOpenPalette={() => setPaletteOpen(true)}
              onNewChat={onNewChatFromRail}
              onSelect={onSidebarSelect}
              onClose={() => setSidebarExpanded(false)}
              isExpanded={sidebarExpanded}
            />
          </aside>
          <div
            className={cn(
              "relative ml-14 flex min-w-0 flex-1 flex-col",
              // In focus (full-screen) mode the chat content (messages +
              // composer) is centred and capped at a readable measure that
              // SCALES with the viewport — never too narrow, never spread
              // edge-to-edge. ``clamp(640px, 75vw, 1100px)`` gives:
              //   - 768px viewport → ~640px (mobile-tight)
              //   - 1280px viewport → ~960px (sweet spot)
              //   - 1920px+ viewport → 1100px capped (no infinite line length)
              // The mini-rail at 48px stays pinned to the left edge —
              // the centring math is only applied to this inner column.
              isFocusMode && "mx-auto w-full max-w-[clamp(640px,75vw,1100px)] px-4",
            )}
          >
            <div className="relative min-h-0 flex-1">
              {/* Share + contents of the thread, pinned top-right once there
                is something to share (Sep 2026). */}
              {currentConversationId && messages.length > 0 ? (
                <ChatTopBar conversationId={currentConversationId} onCitationClick={onCitationClick} />
              ) : null}
              {/* Empty-state pane — replaces the (otherwise blank) messages
                viewport when the conversation has zero turns AND nothing
                is in flight. We keep ``<Messages>`` mounted for the
                streaming case (token-pre-stream feedback would otherwise
                vanish into the empty pane). May 24, 2026 cold-start UX. */}
              {messages.length === 0 && !streamingSlot ? (
                <ChatEmptyState />
              ) : (
                <Messages
                  messages={messages}
                  onCitationClick={onCitationClick}
                  onToolbarAction={onMessageToolbarAction}
                  streamingSlot={streamingSlot}
                  // Inline source rail under every agent message that has
                  // citations. Reuses the same SourceRailSurface mounted at
                  // the top of each workspace hub — clicking a chip navigates
                  // to the destination tab with ``?focus=<doc>&msg=<msg>`` so
                  // the SourceRail chrome stays in lock-step with the chat
                  // history (the user can re-open citations of any historical
                  // answer, not just the latest auto-opened one). May 24,
                  // 2026 — explicit user ask: "dove e come riapro le sources
                  // se le ho chiuse? Non vedo nulla".
                  renderAfterMessage={(m) => {
                    if (m.kind !== "agent") return null;
                    const hasCitations = (m.citations?.length ?? 0) > 0;
                    // ``DisplayResultChips`` returns ``null`` when the
                    // message has no ``displays[]`` so mounting it
                    // unconditionally is cheap. Same pattern as
                    // ``MessageActionChips``: render-cost is one Zustand
                    // subscription on a single message id (no array walk).
                    return (
                      <div className="space-y-1">
                        {m.queryId && m.status !== "streaming" ? (
                          <CompletedActivity queryId={m.queryId} metadata={m.metadata} />
                        ) : null}
                        {hasCitations ? (
                          <div className="mt-2 px-1">
                            {/* Footnote variant — demoted text-only chrome
                              so neighbouring actionable chips (Grafico,
                              MessageActionChips) keep visual priority
                              (June 14, 2026 — user feedback: "il chip
                              si vede poco" because source citations
                              were visually indistinguishable from the
                              actionable chips next to them). */}
                            <SourceRailSurface
                              messageId={m.id}
                              activeFocusId={null}
                              variant="footnote"
                            />
                          </div>
                        ) : null}
                        {/* Display chips — re-entry to the work-area panel
                          for SQL / CAD / banking result attachments.
                          Mirror of the SourceRail chip row above; gives
                          the user a persistent way to re-open the
                          dedicated result view even after they closed
                          it or scrolled past the auto-route window. */}
                        <DisplayResultChips messageId={m.id} />
                        <MessageActionChips queryId={m.queryId ?? null} />
                      </div>
                    );
                  }}
                  renderNewMessagesPill={({ onJumpToBottom }) => (
                    <button
                      type="button"
                      onClick={onJumpToBottom}
                      className="-translate-x-1/2 absolute bottom-4 left-1/2 rounded-full border border-hairline bg-card/95 px-3 py-1.5 font-medium text-ink-1 text-xs shadow-sm backdrop-blur-sm hover:bg-card"
                    >
                      <Trans>↓ Nuovi messaggi</Trans>
                    </button>
                  )}
                />
              )}
            </div>
            {lastPivot ? (
              <div className="flex flex-shrink-0 justify-start px-8 pt-1">
                <ChatUndoChip
                  key={lastPivot.timestamp}
                  label={undoChipLabel(i18n, lastPivot.toTab)}
                  onUndo={() => {
                    revertLastPivot();
                  }}
                  onExpire={() => {
                    // Auto-dismiss after UNDO_CHIP_TTL_MS — pop the history so
                    // the chip vanishes. revertLastPivot would also navigate;
                    // here we want to keep the user on the new tab.
                    useWorkspaceStore.getState().popPivot();
                  }}
                />
              </div>
            ) : null}
            <div data-tour="composer" className="composer flex-shrink-0 px-8 pt-2 pb-5">
              <ChatComposer />
            </div>
          </div>
        </section>

        {/* ── Drag handle (split mode only, never on modal routes) ─────
          Cursor IDE / Figma pattern: invisible 8px hit-strip with a
          glowing orange line on hover/drag. Snap-to-preset (30/38/50/
          62/70%), per-tab memory, full keyboard a11y. May 31, 2026.
          Suppressed entirely on ``_authed/_modal/**`` routes (Settings,
          Integrations, …) — those have no chat/workspace split and the
          dragable hit-strip / native tooltip would be dead chrome.
          June 15, 2026. */}
        {isSplitMode && !isModalRoute ? (
          <SplitDragHandle tab={currentTab} currentRatio={splitRatio} containerRef={shellRowRef} />
        ) : null}

        {/* ── Right pane: Workspace canvas ────────────────────────────────
          Width follows the 3-mode layout:
            - focus      : 0 (hidden)
            - canvas-led : calc(100% - 48px) — chat is just the rail
            - split      : 62% (legacy default for chat-led tabs)
          ``flex-1`` keeps the canvas filling whatever space the chat
          section released, removing the need for explicit width math
          on the workspace. May 30, 2026 — 3-mode adaptive layout. */}
        <section
          className={cn(
            // May 23 polish — unified bg with chat pane (was bg-cream/30
            // vs /40, a 10% alpha diff is imperceptible). Separation now
            // relies on the hairline alone, matching Linear/Notion split
            // panes.
            "relative z-10 flex h-full flex-col bg-cream/40 transition-[width] duration-200 ease-out",
            isFocusMode ? "pointer-events-none w-0 overflow-hidden opacity-0" : "min-w-0 flex-1",
          )}
          aria-label={i18n._(msg`Area di lavoro`)}
          aria-hidden={isFocusMode}
        >
          <WorkspaceTabs rightSlot={isModalRoute || isFocusMode ? null : <LayoutSwitcher />} />
          {/* SOTA HITL workspace pattern (May 2026): action proposals
            routed to the active tab by ``useActionRouter`` materialise
            ABOVE the routed content — Cursor / ChatGPT Canvas pattern.
            Single mount point (not per-route) per CLAUDE.md DRY: the
            surface reads ``selectCurrentTab`` and filters its own
            proposals, returning ``null`` when empty so the underlying
            ``<Outlet>`` keeps the full viewport.

            ``SourceConfirmationSurface`` mounts ABOVE ``ActionFormSurface``
            so the "which file did you mean?" card lands before the diff
            preview it births — the user always sees disambiguation BEFORE
            approval (May 22, 2026 "Acme but got Brini" follow-up). */}
          <SourceConfirmationSurface />
          <ActionFormSurface />
          {/* Persistent per-section help for demo visitors (no-op for real users
            / sections without a guide). Sits above the routed content so it's
            always in view. */}
          <SectionGuide section={currentTab} />
          <div className="relative min-h-0 flex-1 overflow-hidden">
            {/* ``FocusedActionSurface`` returns ``null`` when there is
              no ``?proposal=`` on the URL — the routed hub keeps the
              full viewport. When the param is set, the focused view
              renders absolute-positioned above the ``<Outlet>`` so
              the underlying hub state survives intact and the user
              can dismiss the proposal with the back chevron without
              losing scroll position / filters. Mirror of the inbox
              ``?focus=`` pattern, just one layer higher (May 24,
              2026 — replaces the squashed mini-card design). */}
            <Outlet />
            <FocusedActionSurface />
          </div>
        </section>
        </>
        )}

        {/* ── Right-rail Source Drawer (citation viewer) ────────────────
          Single mount point — `<SourceDrawerSurface>` reads the sources
          store and only renders when the active source's home tab matches
          the active workspace tab. Citation clicks land here via
          `onCitationClick={onCitationClick}` on `<Messages>` above. */}
        <SourceDrawerSurface />

        {/* ── Global Cmd+K command palette (singleton) ────────────────── */}
        <ConversationCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

        {/* ── Share dialog (singleton) — opened by the message toolbar and
            the sidebar row menu via ``useShareDialogStore``. ─────────── */}
        <ShareLinkDialog />

        {/* ── Global ⌘/ keyboard shortcuts overlay (singleton) ─────────── */}
        <KeyboardShortcutsOverlay />

        {/* ── Universal sync toast (singleton, bottom-right) ──────────── */}
        {/* Subscribes to ``SyncStore`` and renders every in-flight sync
          job — email / chat / drive / filesystem — with the same
          progress + counters + cancel widget. Mount once here so a
          job started from any feature (integrations hub, chat
          step-up, automation) gets the same UI. */}
        <UniversalSyncToast />
      </div>
    </div>
  );
}

/**
 * Format the UndoChip label as `"Aperto <Tab>"` (matches the v18 mockup).
 * Falls back to the raw tab id when a def is missing — shouldn't happen
 * in practice, but keeps the chip rendering safe. Takes the active
 * ``i18n`` so the tab label resolves through Lingui (the def's
 * ``labelMsg`` sidecar) when available.
 */
function undoChipLabel(i18n: I18n, tab: import("@ando/core/domain").WorkspaceTab): string {
  const def = getWorkspaceTabDef(tab);
  const tabName = def ? i18n._(def.labelMsg) : tab;
  return i18n._(msg`Aperto ${tabName}`);
}

/**
 * `ChatComposer` — wires the @ando/ui Composer compound to
 * `useComposerSubmit` (Phase 4.C). On submit the hook:
 *   1. optimistically appends the user message to the chat store,
 *   2. calls the (mocked, Phase 5) chat endpoint,
 *   3. navigates the workspace router when the response ships a routing hint,
 *   4. surfaces the UndoChip via the pivot stack.
 */
// Composer quick-prompt chips above the composer — dynamic via
// ``useQuickPrompts(activeWorkspaceTab)`` (BE ``GET /chat/suggestions``). The
// hardcoded ``FALLBACK_QUICK_PROMPTS_DEFS`` set keeps the chips usable when
// the BE is down or the user is offline — labels match the v18 mockup
// `.composer-chips` row (graceful degradation, no flash of empty). Labels
// and prompts are ``MessageDescriptor`` so Lingui extracts them; we resolve
// them inside ChatComposer (which has ``useLingui()`` access) into the
// wire-shaped ``{ id, label: string, prompt: string }`` the BE response
// also produces.
const FALLBACK_QUICK_PROMPTS_DEFS: ReadonlyArray<{
  id: string;
  label: MessageDescriptor;
  prompt: MessageDescriptor;
}> = [
  {
    id: "summarize-week",
    label: msg`Riassumi la settimana`,
    prompt: msg`Riassumi la mia settimana: meeting principali, decisioni e prossimi passi.`,
  },
  {
    id: "today-todo",
    label: msg`Cosa devo fare oggi`,
    prompt: msg`Cosa devo fare oggi? Mostrami priorità, scadenze e meeting.`,
  },
];

/**
 * Human-readable size from bytes (mockup-faithful: "218 KB", "1.2 MB").
 * Caller-side because the FileChipList primitive accepts a pre-formatted
 * string (keeps the primitive stateless + format-agnostic).
 */
function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Slash-command registry — deterministic, FE-owned (BE endpoint TBD). All
// labels Italian to match Andrea's primary locale.
type SlashAction = "help" | "clear" | "sources" | "export";

// Descriptions are ``MessageDescriptor`` so Lingui extracts them; resolved
// at render time inside ``ChatComposer`` (where ``useLingui()`` is
// available). The ``command`` string itself (e.g. ``/help``) is a wire
// token — kept literal across locales.
const SLASH_COMMANDS: ReadonlyArray<{
  id: SlashAction;
  command: string;
  description: MessageDescriptor;
  icon: typeof HelpCircle;
}> = [
  {
    id: "help",
    command: "/help",
    description: msg`Mostra i comandi disponibili`,
    icon: HelpCircle,
  },
  {
    id: "clear",
    command: "/clear",
    description: msg`Pulisci la conversazione corrente`,
    icon: Trash2,
  },
  {
    id: "sources",
    command: "/sources",
    description: msg`Apri il drawer delle fonti`,
    icon: FileText,
  },
  {
    id: "export",
    command: "/export",
    description: msg`Esporta la chat come Markdown`,
    icon: Download,
  },
];

// Slash-command markdown body for `/help` — keeps the registry, command
// names and Italian copy in one canonical place. Built at call-time so
// the ``i18n`` instance is available; ``/help`` invocations resolve each
// line through Lingui.
function buildSlashHelpMarkdown(i18n: I18n): string {
  return [
    `**${i18n._(msg`Comandi disponibili`)}**`,
    `- \`/clear\` — ${i18n._(msg`pulisce la conversazione corrente`)}`,
    `- \`/sources\` — ${i18n._(msg`elenca le fonti correlate all'ultima risposta`)}`,
    `- \`/export\` — ${i18n._(msg`esporta la conversazione in Markdown`)}`,
    `- \`/help\` — ${i18n._(msg`mostra questo elenco`)}`,
  ].join("\n");
}

/** Best-effort UUID — falls back to a Math.random-derived id on older runtimes. */
function freshMessageId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `slash-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

/** Build a synthetic agent message for `/help` — no BE round-trip. */
function buildSlashHelpMessage(i18n: I18n, conversationId: string): MessageView {
  return {
    id: freshMessageId(),
    conversationId,
    role: "agent",
    content: buildSlashHelpMarkdown(i18n),
    timestamp: new Date().toISOString(),
    status: "sent",
  };
}

/**
 * Pick a short, human-readable label for a source — mirrors the precedence
 * used by `SourceRailSurface` so the `/sources` bubble matches what the
 * citation drawer shows.
 */
function pickSourceDisplay(
  i18n: I18n,
  src: {
    subject?: string;
    source_file?: string;
    url?: string;
    doc_id: string;
  },
): string {
  return src.subject ?? src.source_file ?? src.url ?? src.doc_id ?? i18n._(msg`Senza titolo`);
}

/**
 * Pick a short snippet for a source — emails carry `email_snippet`; web
 * results carry `content`; chat messages carry `text`. Trimmed to ~120 chars.
 */
function pickSourceSnippet(src: {
  email_snippet?: string;
  content?: string;
  text?: string;
}): string {
  const raw = src.email_snippet ?? src.content ?? src.text ?? "";
  if (!raw) return "";
  return raw.length > 120 ? `${raw.slice(0, 120).trimEnd()}…` : raw;
}

/**
 * Build a synthetic agent message for `/sources`. Reads the last
 * agent-turn that carries sources from the per-conversation message list.
 */
function buildSlashSourcesMessage(
  i18n: I18n,
  conversationId: string,
  messages: ReadonlyArray<MessageView>,
): MessageView {
  const lastAgentWithSources = [...messages]
    .reverse()
    .find((m) => m.role === "agent" && (m.sources?.length ?? 0) > 0);

  let content: string;
  if (!lastAgentWithSources || !lastAgentWithSources.sources?.length) {
    content = i18n._(msg`Nessuna fonte associata all'ultima risposta.`);
  } else {
    const list = lastAgentWithSources.sources;
    const lines: string[] = [`**${i18n._(msg`Fonti dell'ultima risposta`)}** (${list.length})`];
    list.forEach((src, i) => {
      const display = pickSourceDisplay(i18n, src);
      const snippet = pickSourceSnippet(src);
      lines.push(`${i + 1}. ${display}${snippet ? ` — ${snippet}` : ""}`);
    });
    content = lines.join("\n");
  }

  return {
    id: freshMessageId(),
    conversationId,
    role: "agent",
    content,
    timestamp: new Date().toISOString(),
    status: "sent",
  };
}

/**
 * Built-in "Standard" mode shown when the user has no custom modes yet.
 *
 * V1 parity (May 24, 2026): the BE catalog (`GET /agent-modes`) returns
 * only user/org/global custom profiles. The "Standard" tutto-fare mode
 * is a FE-built-in fallback so the dropdown is never empty + locked to
 * the literal id ``"standard"`` (matches ConversationConfig defaults).
 *
 * The label / description are Lingui ``MessageDescriptor``s — resolved
 * inside ``ChatComposer`` (which has ``useLingui()`` access) into the
 * wire-shape ``{ id, label: string, description?: string }`` that
 * ``ComposerMode`` expects. The full mode list is the
 * ``[BUILTIN_STANDARD, ...customModes]`` array — see ``useComposerModes``
 * below.
 */
const BUILTIN_STANDARD_MODE_DEF: {
  readonly id: "standard";
  readonly label: MessageDescriptor;
  readonly description: MessageDescriptor;
} = {
  id: "standard",
  label: msg`Ando · Standard`,
  description: msg`Tutto-fare`,
};

function ChatComposer() {
  const { i18n } = useLingui();
  const [value, setValue] = useState("");
  const { submit, pending } = useComposerSubmit();

  // Active workspace tab — drives both the per-tab quick-prompt fetch
  // (calendar → meeting brief, finance → crediti aperti) AND the
  // tab-aware Focus toggle below (May 30, 2026 3-mode layout: each tab
  // remembers its own preferred chat width).
  const activeWorkspaceTab = useWorkspaceStore(selectActiveTab);
  const currentTab = useWorkspaceStore(selectCurrentTab);

  // Focus/canvas-led/split layout state. Toggle lives in the composer
  // footer beside the connectors-active status line so it's discoverable
  // without leaving the chat surface.
  const { isFocus: isFocusMode } = useResolvedChatLayout(currentTab);
  const toggleFocusForTab = useChatLayoutStore((s) => s.toggleFocusForTab);
  const toggleFocus = useCallback(
    () => toggleFocusForTab(currentTab),
    [currentTab, toggleFocusForTab],
  );
  const { data: quickPromptsData } = useQuickPrompts(activeWorkspaceTab);
  // Resolve the localized fallback set lazily so a re-render only re-walks
  // it when ``i18n`` (locale) changes. Shape mirrors the BE ``ChatSuggestion``
  // wire contract so the consumer can read ``.label`` / ``.prompt`` directly.
  const fallbackQuickPrompts = useMemo(
    () =>
      FALLBACK_QUICK_PROMPTS_DEFS.map((p) => ({
        id: p.id,
        label: i18n._(p.label),
        prompt: i18n._(p.prompt),
      })),
    [i18n],
  );
  const quickPrompts = quickPromptsData?.suggestions ?? fallbackQuickPrompts;

  // Connector footer — counts the satisfied capabilities from the manifest
  // and renders an Italian-correct singular/plural label. Falls back to
  // "Nessuna fonte connessa" while the manifest is loading.
  const activeConnectorsCount = useActiveConnectorsCount();
  const connectorsLabel = formatActiveConnectorsLabel(i18n, activeConnectorsCount);

  // Deep-link prefill — ``?prompt=…`` URL param seeds the textarea exactly
  // once when it lands. Tracked via a ref (instead of depending on
  // ``value``) so a manual clear afterwards never re-seeds the textarea.
  const deepLinkPrompt = useDeepLinkPrompt();
  const deepLinkSeededRef = useRef(false);
  useEffect(() => {
    if (deepLinkPrompt && !deepLinkSeededRef.current) {
      deepLinkSeededRef.current = true;
      setValue((prev) => (prev === "" ? deepLinkPrompt : prev));
    }
  }, [deepLinkPrompt]);

  // Store-side draft bridge — surfaces inline "set the composer to X"
  // calls from anywhere in the app (message edit, ask-Ando prefills,
  // ai-proposal CTAs, …) via the ``composerDraft`` slice. We
  // unidirectionally pipe ``store → local state`` and clear the slice
  // immediately so a stale value can't re-fire on a later render. The
  // local ``value`` stays the textarea source-of-truth.
  const composerDraft = useChatStore(selectComposerDraft);
  const setComposerDraft = useChatStore((s) => s.setComposerDraft);
  useEffect(() => {
    if (!composerDraft) return;
    setValue(composerDraft);
    setComposerDraft("");
  }, [composerDraft, setComposerDraft]);

  // Hooks wired to the chat store (Phase 6.B.2 + 6.B.1).
  const { config, set: setConfig } = useComposerConfig();
  const { access } = useFeatureAccess();
  const {
    images,
    files,
    remove: removeAttachment,
    clear: clearAttachments,
    addFiles,
    getDragHandlers,
    getPasteHandler,
    isDragOver,
    isUploading,
    lastError,
  } = useChatAttachments();

  // ── Voice-note recording (June 1, 2026) ─────────────────────────────
  // Mic button drives MediaRecorder; on stop we upload the WebM blob
  // through ``addFiles`` so it rides the standard chat-file pipeline
  // (MinIO + content_items + WSSendQuery attachment threading). The
  // recorder hook is mounted once per composer instance.
  const audio = useAudioRecorder();
  const handleVoiceToggle = useCallback(async () => {
    if (audio.recording) {
      const blob = await audio.stop();
      if (!blob) return;
      // Pick a friendly extension off the negotiated MIME so the chip
      // labels read ``vocale-…webm`` instead of ``vocale-…audio``.
      const ext = audio.mimeType?.includes("mp4")
        ? "m4a"
        : audio.mimeType?.includes("ogg")
          ? "ogg"
          : audio.mimeType?.includes("wav")
            ? "wav"
            : "webm";
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const file = new File([blob], `vocale-${ts}.${ext}`, {
        type: blob.type,
      });
      await addFiles([file]);
    } else {
      await audio.start();
    }
  }, [audio, addFiles]);

  const navigate = useNavigate();

  // Drive folder picker — fetches the user's tracked folders across all
  // connected providers. Selection populates ``config.folderFilter`` which
  // the WS query envelope already forwards as ``folder_filter``.
  // Jun 8, 2026 — unified scope folders (Drive + filesystem-desktop).
  // The endpoint returns each row with a ``filter_key`` that is THE
  // value to push on the WS ``folder_filter[]`` envelope:
  //   - Drive: provider-native folder_id (matches Weaviate ``folder_id``)
  //   - Filesystem: ``fs://<abs_path>`` (BE splits the prefix and
  //     switches to ``folder_path startswith`` Weaviate filter)
  // This single hook supersedes the older ``useDriveFolders`` Drive-only
  // aggregator for the composer scope-picker; ``useDriveFolders`` stays
  // around only for any legacy caller that needs the Drive-specific
  // shape (rare; future cleanup candidate).
  const scopeFoldersQuery = useScopeFolders();
  const scopeFolderItems: readonly ScopeFolderItem[] = useMemo(
    () =>
      (scopeFoldersQuery.data ?? []).map((f) => ({
        id: f.filter_key,
        name: f.name,
        path: f.path,
        provider: f.provider,
      })),
    [scopeFoldersQuery.data],
  );

  // June 2026 — connected-but-untracked providers surface: the BE only
  // returns folders the user has explicitly tracked, so a user with
  // Google Drive connected but no tracked folders would see nothing
  // for that provider. We compute the diff ourselves so the picker can
  // render an explanatory row ("Google Drive: nessuna cartella
  // tracciata · Traccia") instead of a silent gap.
  const driveConnections = useCapabilityConnections("drive");
  const untrackedConnectedProviders = useMemo<
    ReadonlyArray<"google_drive" | "onedrive" | "sharepoint">
  >(() => {
    type Prv = "google_drive" | "onedrive" | "sharepoint";
    // Only consider Drive providers when computing the "connected but
    // untracked" set — filesystem rows are also in ``scopeFolderItems``
    // now but don't belong to the Drive capability.
    const trackedSet = new Set<Prv>();
    for (const f of scopeFolderItems) {
      if (
        f.provider === "google_drive" ||
        f.provider === "onedrive" ||
        f.provider === "sharepoint"
      ) {
        trackedSet.add(f.provider);
      }
    }
    const connectedSet = new Set<Prv>();
    for (const c of driveConnections.connections) {
      // ``asDriveProvider`` narrows the raw connection.provider string
      // (which also includes ``notion`` — not in scope here).
      const narrowed = asDriveProvider(c.provider ?? null);
      if (narrowed === null || narrowed === "notion") continue;
      connectedSet.add(narrowed);
    }
    // Preserve a stable display order (Google → OneDrive → SharePoint).
    return (["google_drive", "onedrive", "sharepoint"] as const).filter(
      (p) => connectedSet.has(p) && !trackedSet.has(p),
    );
  }, [driveConnections.connections, scopeFolderItems]);

  // Document picker (June 2026) — debounced live search backed by the
  // mention registry (``GET /api/v1/search/mention?kinds=document``).
  // Selection populates ``config.scopedDocuments`` which ``useSendQuery``
  // merges into the WS envelope's ``mentions[]`` field with
  // ``kind: "document"``. No new BE field — reuses ``build_mention_filters``.
  const [scopeDocQuery, setScopeDocQuery] = useState("");
  const [scopeDocPickerOpen, setScopeDocPickerOpen] = useState(false);
  const scopeDocSearch = useMentionSearch({
    query: scopeDocQuery,
    open: scopeDocPickerOpen,
    kinds: ["document"],
    limit: 12,
  });
  const scopeDocumentResults: readonly ScopeDocumentItem[] = useMemo(
    () =>
      scopeDocSearch.results.map((r) => ({
        id: r.id,
        display: r.display,
        subtitle: r.subtitle ?? undefined,
        provider: r.provider ?? undefined,
        folderPath: r.folder_path ?? undefined,
      })),
    [scopeDocSearch.results],
  );
  // Mirror the picker open state into ``useMentionSearch`` so it stops
  // firing when the popover closes. The picker primitive doesn't expose
  // its own open signal, so we listen to a focused query change as a
  // best-effort proxy + flip on first interaction below.
  const handleScopeDocQueryChange = useCallback((q: string) => {
    setScopeDocPickerOpen(true);
    setScopeDocQuery(q);
  }, []);

  // ── Ando Connect "interroga un'area" scope (June 14, 2026) ───────────
  // Areas the user can query in chat = owned + shared-with-me, merged into the
  // picker's flat option shape. Fetched through the SAME cached queries the
  // Connect hub uses (TanStack Query dedupes), so the pill is discoverable for
  // everyone — an empty list shows the "Vai a Connect" onboarding state rather
  // than hiding the affordance. Selecting one pins ``config.connectSpace``;
  // every turn then answers ONLY from that area (BE re-validates membership).
  const ownedAreas = useConnectSpaces();
  const sharedAreas = useSharedConnectSpaces();
  const connectAreaOptions = useMemo<ConnectAreaOption[]>(() => {
    const owned = (ownedAreas.data ?? []).map((s) => ({
      id: s.id,
      name: s.name,
      ownerOrgName: null,
    }));
    const shared = (sharedAreas.data ?? []).map((s) => ({
      id: s.id,
      name: s.name ?? i18n._(msg`Area condivisa`),
      ownerOrgName: s.owner_org_name ?? null,
    }));
    return [...owned, ...shared];
  }, [ownedAreas.data, sharedAreas.data, i18n]);
  const connectAreasLoading = ownedAreas.isLoading || sharedAreas.isLoading;

  // Streaming state — drives Send → Stop swap + composer disabled hint.
  const currentId = useChatStore(selectCurrentConversationId);
  const isStreaming = useChatStore(selectIsStreamingFor(currentId));
  const stopQuery = useStopQuery();

  // Mode selector — derives from ConversationConfig.agentModeVersionId so
  // the choice persists across sidebar switches (audit must-not-lose #3).
  const activeModeId = config.agentModeVersionId ?? "standard";

  // V1 parity (May 24, 2026): BE-driven mode catalog from
  // ``GET /agent-modes`` (gated by ``modes`` capability). The
  // ``BUILTIN_STANDARD_MODE`` ships as the first entry so the dropdown
  // is never empty for tenants without custom profiles. Selecting a
  // custom mode triggers ``default_config`` auto-apply (search-power,
  // thinking, web, focus) — see ``handleModeChange`` below.
  const { modes: customModes } = useAgentModes();
  // June 1, 2026 — CRITICAL BUG FIX: the dropdown id MUST be the
  // ``current_version.id`` (UUID of the version row in
  // ``agent_mode_versions``), NOT the parent ``Mode.id``. The wire field
  // is ``agent_mode_version_id`` and the BE looks it up via
  // ``get_agent_mode_version(version_id, …)`` — passing the parent id
  // silently fails the lookup ("Agent mode version not found") and the
  // overlay is dropped. The user sees the label change but the mode
  // never reaches the agent. ``Mode.id`` stays accessible via
  // ``current_version.agent_mode_id`` if any caller needs the parent.
  const composerModes = useMemo<ComposerMode[]>(() => {
    // Guard against draft/malformed modes that don't yet expose a
    // ``current_version`` — silently drop them from the picker so a
    // stale BE row can't crash the dropdown. Future BE deploys can
    // expose ``is_active`` already covers most of this, but the
    // defensive flatMap is one line + outlives schema churn.
    const mapped: ComposerMode[] = customModes.flatMap((m) =>
      m.current_version?.id
        ? [
            {
              id: m.current_version.id,
              label: i18n._(msg`Ando · ${m.name}`),
              description: m.description ?? undefined,
            },
          ]
        : [],
    );
    const builtin: ComposerMode = {
      id: BUILTIN_STANDARD_MODE_DEF.id,
      label: i18n._(BUILTIN_STANDARD_MODE_DEF.label),
      description: i18n._(BUILTIN_STANDARD_MODE_DEF.description),
    };
    return [builtin, ...mapped];
  }, [customModes, i18n]);

  const handleModeChange = useCallback(
    (id: string) => {
      // V1 ``default_config`` auto-apply (atomic): a single ``setConfig``
      // call merges agentModeVersionId + inferred toggles in one shot.
      // Zustand sets are sync, but keeping them in one call avoids any
      // partial-state read by downstream subscribers (e.g. a CharCount
      // memo that runs between the two setters on slow devices).
      //
      // May 26, 2026 — two-tier collapse: prefer the new ``mode`` field;
      // fall back to deriving from legacy ``search_power`` /
      // ``thinking_mode`` so older rows persisted before the migration
      // still hydrate sensibly.
      if (id === "standard") {
        setConfig({ agentModeVersionId: id });
        return;
      }
      // Lookup by version id (matches the new ``composerModes`` keying
      // — see comment above).
      const picked = customModes.find((m) => m.current_version.id === id);
      const dflt = picked?.current_version?.default_config;
      if (!dflt) {
        setConfig({ agentModeVersionId: id });
        return;
      }
      const inferredMode: "fast" | "deep" | undefined =
        dflt.mode ??
        (dflt.search_power === "expert" || dflt.thinking_mode === true
          ? "deep"
          : dflt.search_power
            ? "fast"
            : undefined);
      setConfig({
        agentModeVersionId: id,
        ...(inferredMode ? { mode: inferredMode } : {}),
        ...(typeof dflt.web_search_mode === "boolean"
          ? { webSearchMode: dflt.web_search_mode }
          : {}),
        ...(dflt.focus ? { focus: dflt.focus } : {}),
      });
    },
    [customModes, setConfig],
  );

  // Inline error banner — surfaces useChatAttachments.lastError plus any
  // local "input troppo lungo" warning. Dismissible.
  const [dismissedError, setDismissedError] = useState<string | null>(null);
  const errorMessage = lastError && lastError !== dismissedError ? lastError : null;

  // Slash command palette — open whenever the draft starts with `/` and the
  // first token is still being typed (no whitespace yet). Descriptions are
  // resolved through Lingui at filter-time so the menu reads the active
  // locale (BE-driven slash commands are still TBD — these are FE-owned).
  const slashQuery = value.startsWith("/") && !value.includes(" ") ? value.slice(1) : null;
  const slashCandidates =
    slashQuery !== null
      ? SLASH_COMMANDS.filter((c) =>
          c.command.toLowerCase().includes(`/${slashQuery.toLowerCase()}`),
        ).map((c) => ({
          id: c.id,
          command: c.command,
          description: i18n._(c.description),
          icon: c.icon,
        }))
      : [];
  const slashOpen = slashQuery !== null;

  // ── Mention autocomplete (``@`` trigger) ──────────────────────────────
  //
  // SOTA May 2026: the composer migrated from a textarea + regex-driven
  // overlay to Tiptap (``Composer.RichEditor``). Mentions are now
  // first-class ProseMirror nodes (chips), so we no longer maintain a
  // regex anchored at end-of-string or a parallel ``selectedMentions``
  // array that can diverge from the visible draft — the editor walks the
  // doc on every keystroke and emits the resolved list via
  // ``onChange(plainText, mentions)``.
  const [selectedMentions, setSelectedMentions] = useState<SendQueryMention[]>([]);

  // Imperative handle on the Composer ``<form>`` — Tiptap's ``onSubmit``
  // callback fires from inside ProseMirror's keydown contract (no native
  // ``<form>`` traversal available), so we forward to ``requestSubmit()``.
  const formRef = useRef<HTMLFormElement | null>(null);

  // Async fetcher handed to Tiptap's mention suggestion plugin. Stable
  // identity (no deps) keeps the editor from re-mounting on each render.
  const onMentionQuery = useCallback(async (q: string): Promise<MentionAutocompleteResult[]> => {
    const response = await searchMentions({ q, limit: 10 });
    return [...response.results];
  }, []);

  // June 3, 2026 — allow attachment-only sends (voice-note without
  // typed text). Audio + image are first-class turn content; the BE
  // persists the user message with the attachment ref regardless of
  // the text body.
  const hasAttachments = images.length > 0 || files.length > 0;
  const canSend =
    !slashOpen &&
    (value.trim().length > 0 || hasAttachments) &&
    !pending &&
    !isUploading &&
    !isStreaming;
  const dragHandlers = getDragHandlers();
  const pasteHandler = getPasteHandler();

  // v1 parity (May 23, 2026):
  //   - Mode lock after first user turn — read messages.length off the store
  //     so the ModeSelector freezes the moment the conversation has any
  //     history (prevents mid-thread profile drift).
  //   - Banking-aware placeholder — when the user has the open-banking
  //     capability satisfied, hint the user toward banking-shaped queries.
  //     TODO: conversation-level banking-tag detection (v1 reads
  //     ``useBankConnections().some(sync_status==="active")`` from the
  //     conversation context — we only have capability state here for now).
  const messagesCount = useChatStore((s) =>
    currentId ? (s.messagesByConversation[currentId]?.length ?? 0) : 0,
  );
  const isModeLocked = messagesCount > 0;
  const hasBankingHint = useHasCapability("open_banking");

  // Jun 8, 2026 — composer placeholder personalisation, refined after
  // UI/UX review:
  //   1. Earned-personalisation rule: in COLD-START the greeting
  //      "Buongiorno {firstName}." already personalises the surface →
  //      the placeholder stays generic to avoid name echo (Linear /
  //      Notion convention: one personalised anchor per surface). Once
  //      the user has sent a message the greeting scrolls out and the
  //      personalised placeholder becomes a useful re-anchor.
  //   2. Loading anti-flicker: only promote to the personalised copy
  //      after ``useProfile`` reaches ``isSuccess``. Mid-fetch the
  //      placeholder shows the generic variant — no "" → name swap
  //      visible at first paint.
  //   3. Single source of truth: ``firstNameOf`` shared with the
  //      cold-start greeting (``ChatEmptyState``). DRY.
  const composerUserId = useChatUserId();
  const capabilityManifest = useCapabilityManifest();
  const activeProjectId = useProjectsStore((state) => state.currentProjectId);
  const tramaLakehouseConfigured = isTramaLakehouseConfigured(
    capabilityManifest.data, composerUserId, capabilityManifest.isError,
    config.selectedConnectionIds, config.connectSpace?.id ?? null, activeProjectId,
  );
  const composerProfile = useProfile(composerUserId);
  const personalisePlaceholder = composerProfile.isSuccess && messagesCount > 0;
  const composerFirstName = personalisePlaceholder ? firstNameOf(composerProfile.data?.name) : "";

  // v1 parity (shipped May 30, 2026): CAD spatial filter. The Tools menu
  // "Analisi CAD" entry opens ``Composer.CadAnalysisModal`` (DXF viewer +
  // drag-a-rectangle). The resolved rectangle is the wire-ready
  // ``spatial_filter`` shape and rides the next message envelope.
  const [cadSpatialFilter, setCadSpatialFilter] = useState<CadSpatialFilterResult | null>(null);
  // Phase 5 (Jun 12, 2026 SOTA — CAD Big Bang) — feature-filter chip,
  // sibling of the spatial filter. Carries the multi-select form the
  // user filled in on the modal's "Filtro feature" tab. Rides one send
  // then the composer clears it (per-message contract, same as spatial).
  const [cadFeatureFilter, setCadFeatureFilter] = useState<CadFeatureFilterResult | null>(null);
  const [cadModalOpen, setCadModalOpen] = useState(false);

  // June 11, 2026 — NO auto-focus-switch on CAD attach. The
  // ``focus="standard"`` BE pipeline already includes the
  // ``internal_factdim`` connector via ``INTERNAL_ENTERPRISE_PROVIDERS``
  // (see ``select_enterprise_connections`` in enterprise_pipeline.py)
  // — that's where ``v_cad_instances`` lives, which is the only view
  // with the ``position_x`` / ``position_y`` columns the spatial
  // filter targets. Forcing ``focus="structured"`` would EXCLUDE
  // ``internal_factdim`` and break the very query the user attached
  // the filter for. ``spatial_filter`` is propagated to the
  // PostRAG enterprise enrichment regardless of focus mode, so the
  // simplest correct behaviour is to leave the focus alone.

  // May 28, 2026 — perceived-latency fix on the Composer.ToolsMenu
  // dropdown. Handlers are reference-stable so the React.memo'd menu
  // doesn't churn on every streaming chunk arrival.
  const handleToggleWebSearch = useCallback(
    (next: boolean) => setConfig({ webSearchMode: next }),
    [setConfig],
  );
  const handleOpenCad = useCallback(() => setCadModalOpen(true), []);
  // Jul 2026 — TERRA β (experimental relational-RAG engine). Admin-only
  // lab toggle: the row exists in ``ToolsMenu`` ONLY when we pass the
  // handler, and we pass it ONLY for platform admins. The admin check
  // lives HERE (apps/client wiring) — ``@ando/ui`` stays role-agnostic.
  // Same ``isPlatformAdmin(getSession())`` gate as ``UserMenuButton``;
  // memoized because this component re-renders on every stream chunk and
  // the session cookie is immutable for the lifetime of the mount.
  const isTerraAdmin = useMemo(() => isPlatformAdmin(getSession()), []);
  const handleChangeTramaInfrastructure = useCallback(
    (next: "current" | "lakehouse") => setConfig({ terraMode: true, tramaInfrastructure: next }),
    [setConfig],
  );
  const handleToggleTerra = useCallback(
    (next: boolean) => setConfig({ terraMode: next }),
    [setConfig],
  );
  // June 11, 2026 — connector selection migrated into the Ambito
  // popover ("Dati" tab). The standalone ``connectorPickerOpen`` state
  // + inline ``Composer.ConnectorPicker`` were removed: the Ambito tab
  // owns the multi-select UX and pipes the result into
  // ``selectedConnectionIds`` (with the focus auto-toggle below). Kept
  // ``useStructuredDataConnectors`` here so the popover renders the
  // same list and the TanStack cache stays shared.
  const lakehouseScope = config.tramaInfrastructure === "lakehouse";
  // Reuse the authenticated configuration hint before scope selection.
  // Listing all connected sources never broadens the pilot's one-account access.
  const lakehouseConnectionId = isTramaLakehouseConfigured(
    capabilityManifest.data, composerUserId, capabilityManifest.isError,
    [], config.connectSpace?.id ?? null, activeProjectId,
  ) ? capabilityManifest.data?.trama_lakehouse?.connection_id : undefined;
  const { connectors: structuredConnectors } = useStructuredDataConnectors(lakehouseScope, lakehouseConnectionId);

  // CAD analysis — the DXF library, fetched only while the modal is open.
  // The CAD modal lists drawings that finished ingestion and have a
  // resolvable MinIO object — the user picks one and drags an area for
  // the LLM ``spatial_filter`` ride-along.
  const { files: dxfFiles, isLoading: dxfFilesLoading } = useDxfFiles({
    enabled: cadModalOpen,
  });
  const cadFiles: CadModalFile[] = useMemo(
    () =>
      dxfFiles
        // Accept both the DB-native ``completed`` and the BE-aliased
        // ``indexed`` (``list_dxf_files`` rewrites the value for the UI;
        // see ``ContentItemQueries.list_dxf_files``). Filtering by
        // either rather than the exhaustive non-failure set keeps the
        // modal aligned with the Tecnico hub badge ("Indicizzato") —
        // user feedback Jun 8: empty CAD picker.
        .filter((f) => f.status === "completed" || f.status === "indexed")
        .map((f) => ({
          id: f.content_item_id,
          filename: f.filename,
          // Compose the same-origin proxy URL on the fly (the list
          // endpoint doesn't echo ``download_url`` — only ``/explore``
          // does, and the modal can't pay an N+1 explore fetch). Mirrors
          // the enrichment in ``technical.tsx``.
          downloadUrl:
            f.minio_object_key && f.minio_bucket
              ? `/api/v1/documents/minio/file/${encodeURIComponent(
                  String(f.minio_bucket),
                )}/${String(f.minio_object_key).split("/").map(encodeURIComponent).join("/")}/proxy`
              : (f.download_url ?? null),
        })),
    [dxfFiles],
  );

  // Derive the active-mode chip set from the persisted ConversationConfig.
  // Each toggle on the config slice surfaces as a dismissible chip; click
  // the X → flips the boolean back off via setConfig.
  const chips: ComposerChip[] = [];
  // May 26, 2026 — two-tier collapse: the "Pensiero esteso" chip is gone
  // (absorbed into the ``DeepModePill`` toggle in the composer Bar). The
  // pill itself advertises the active state — no need for a chip echo.
  // Per-tool icons differentiate active tools at a glance.
  if (config.webSearchMode) chips.push({ id: "web", label: i18n._(msg`Ricerca web`), icon: Globe });
  if (config.folderFilter.length > 0) {
    const folderCount = config.folderFilter.length;
    chips.push({
      id: "folders",
      label: i18n._(msg`${folderCount} cartelle`),
      icon: Folder,
    });
  }
  if (config.scopedDocuments.length > 0) {
    const docCount = config.scopedDocuments.length;
    chips.push({
      id: "documents",
      label: i18n._(msg`${docCount} file`),
      icon: FileText,
    });
  }
  if (cadSpatialFilter) {
    const cadFile = cadSpatialFilter.source_file;
    chips.push({
      id: "cad",
      label: i18n._(msg`CAD · ${cadFile}`),
      icon: Crosshair,
    });
  }
  // June 14, 2026 — Ando Connect "interroga un'area" scope. A read-only,
  // accent-toned chip naming the shared Space this conversation is querying.
  // The ``Blend`` glyph matches the Connect nav icon. X dismiss → back to the
  // user's own knowledge (``connectSpace: null``).
  if (config.connectSpace) {
    const areaName = config.connectSpace.name;
    chips.push({
      id: "connect",
      label: i18n._(msg`Area · ${areaName}`),
      icon: Blend,
      tone: "accent",
    });
  }
  // Jul 2026 — TERRA β experimental retrieval engine (admin toggle in
  // Strumenti). Accent tone: it changes the contract of the next answer
  // Echoed whenever the config flag is
  // ON — even if the session somehow lost the admin role with the flag
  // persisted, the chip stays visible so the X remains the escape hatch.
  if (config.terraMode) {
    chips.push({
      id: "terra",
      label: i18n._(msg`TRAMA β`),
      icon: FlaskConical,
      tone: "accent",
    });
  }

  // ChipBar de-dup: ``web`` / ``cad`` / ``terra`` are already
  // counted in the Tools badge, so echoing them as chips too was a double
  // signal. The ChipBar shows only the SCOPE chips (Ambito folders/docs, Area).
  const scopeChips = chips.filter((c) => !["web", "cad", "terra"].includes(c.id));

  const audioFiles = files.filter((f) => f.mime_type.startsWith("audio/"));
  const fileChips: FileChip[] = files.filter((f) => !f.mime_type.startsWith("audio/")).map((f) => ({
    key: f.content_id,
    filename: f.filename,
    size: fmtSize(f.size_bytes),
    status: f.ingestion_status,
    uploading: f.uploading,
    extractedCount: f.extracted_count,
  }));

  // Slash-command actions — wired May 2026.
  //   - `/clear`   → wipes the current conversation's messages from the
  //                  store (FE-only soft reset; sidebar entry survives).
  //   - `/help`    → appends a synthetic agent message listing commands.
  //   - `/sources` → appends a synthetic agent message rendering the last
  //                  agent turn's sources as a numbered list.
  //   - `/export`  → POST `/chat/{id}/export`; download the Markdown blob.
  const appendMessageTo = useChatStore((s) => s.appendMessageTo);
  const clearMessagesFor = useChatStore((s) => s.clearMessagesFor);
  const exportMutation = useExportConversation();

  const runSlashCommand = (id: string) => {
    setValue("");
    if (!currentId) return;
    switch (id as SlashAction) {
      case "clear": {
        // FE-only soft reset: clear local draft + attachments + messages.
        // Real conversation deletion lives in `useConversationMutations`.
        clearAttachments();
        clearMessagesFor(currentId);
        break;
      }
      case "help": {
        appendMessageTo(currentId, buildSlashHelpMessage(i18n, currentId));
        break;
      }
      case "sources": {
        const messages = useChatStore.getState().messagesByConversation[currentId] ?? [];
        appendMessageTo(currentId, buildSlashSourcesMessage(i18n, currentId, messages));
        break;
      }
      case "export": {
        exportMutation.mutate(currentId);
        break;
      }
    }
  };

  // ── Compact mode (May 24, 2026) ───────────────────────────────────────
  //
  // Quick-prompts now LIVE-IN the composer behind the `+` attach popover
  // (no more 36px row above the card on idle). Composer chrome stays at
  // ~88px on idle, auto-grows to ~225px while typing, with a ⌘⇧E escape
  // to a fullscreen sheet for long drafts.
  //
  // ``isComposerFocused`` flips on focus-within so the auxiliary controls
  // (Header / footer) can fade in only when the user is actively
  // composing — Linear / Slack pattern.
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const composerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = composerRef.current;
    if (!node) return;
    const onFocusIn = () => setIsComposerFocused(true);
    const onFocusOut = () => {
      // Defer one tick — clicking inside a popover briefly moves focus to
      // body; we don't want to flicker the chrome off then back on.
      window.requestAnimationFrame(() => {
        if (!node.contains(document.activeElement)) setIsComposerFocused(false);
      });
    };
    node.addEventListener("focusin", onFocusIn);
    node.addEventListener("focusout", onFocusOut);
    return () => {
      node.removeEventListener("focusin", onFocusIn);
      node.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  // Show quick-prompts only when the user is actively composing AND has
  // no text yet — a focus-triggered suggestion strip, not a permanent
  // 36px row. On blur the strip fades out via the same focus tracker.
  const showQuickPrompts =
    isComposerFocused && value.length === 0 && images.length === 0 && files.length === 0;

  // ⌘⇧E — fullscreen composer overlay for long drafts (briefs, contract
  // text). The composer card stays mounted; we just wrap it in a modal
  // shell + bump the editor height when ``expandedComposer`` is true.
  // Esc closes the overlay (preserves draft) — submitting also closes.
  const [expandedComposer, setExpandedComposer] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const metaShift = (e.metaKey || e.ctrlKey) && e.shiftKey;
      if (metaShift && e.key.toLowerCase() === "e") {
        e.preventDefault();
        setExpandedComposer((v) => !v);
      } else if (e.key === "Escape" && expandedComposer) {
        e.preventDefault();
        setExpandedComposer(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expandedComposer]);

  // Composer wrapper className:
  // - Compact (default): just transparent shell, sits inline in the chat pane
  // - Expanded (⌘⇧E): centered modal overlay with backdrop, 720×min-480 card
  // Hover/expanded composer wants MAX focus on the input — the underlying
  // workspace stays just legible enough for context but the composer card
  // is the unambiguous foreground. Two SOTA knobs tuned together:
  //   - Scrim:  ``bg-ink-1/55`` (≈ rgba(15,23,42,.55)) + ``backdrop-blur-xl``
  //             (16 px) — same combo used by Linear command palette and
  //             Apple Spotlight; matte enough to kill ambient noise without
  //             going black-modal heavy.
  //   - Card:   fully opaque ``bg-cream`` (was 95% — anything sub-100% reads
  //             as "draft / not focused"), a 1 px hairline ring to crisp the
  //             edge over the blurred substrate, and a tall ``shadow-2xl``
  //             plus a soft outer glow so the surface visibly floats.
  // The result: the user's eye lands on the composer instantly; the page
  // behind is recognisable but un-readable. Jun 4 2026 user feedback.
  const wrapperClassName = expandedComposer
    ? "fixed inset-0 z-[1000] grid place-items-center bg-ink-1/55 backdrop-blur-xl p-4 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
    : "";
  const innerClassName = expandedComposer
    ? "relative w-[min(720px,92vw)] max-h-[88vh] overflow-y-auto rounded-2xl bg-cream p-4 shadow-2xl ring-1 ring-black/5 motion-safe:animate-in motion-safe:zoom-in-95 motion-safe:fade-in motion-safe:duration-200"
    : "";

  // The composer sits inside a ``<motion.aside>`` (framer-motion) so
  // the chat sidebar can slide in/out. Framer applies ``transform`` to
  // that ancestor, which per CSS spec makes ``position: fixed`` resolve
  // against the transformed parent instead of the viewport — the
  // expanded-composer scrim ends up covering only the sidebar, with the
  // workspace pane bleeding through on the right (Jun 4 2026 user
  // screenshot). Same constraint Radix Dialog solves with a Portal.
  //
  // When ``expandedComposer === true`` we portal the wrapper to
  // ``document.body`` so ``fixed inset-0`` is relative to the viewport
  // again. State (Tiptap editor instance, focus, slash popups) survives
  // because React Portals preserve the component tree even when the
  // DOM node moves.
  const composerBody = (
    <>
      {expandedComposer ? (
        <button
          type="button"
          aria-label={i18n._(msg`Chiudi composer espanso`)}
          onClick={() => setExpandedComposer(false)}
          className="absolute inset-0 cursor-default"
        />
      ) : null}
      <div className={innerClassName}>
        {expandedComposer ? (
          <div className="mb-2 flex items-center justify-between">
            <span className="font-medium text-[11px] text-ink-3 uppercase tracking-[0.05em]">
              <Trans>Composer esteso</Trans>
            </span>
            <span className="font-mono text-[10px] text-ink-3/70">
              <Trans>⌘⇧E per chiudere · Esc</Trans>
            </span>
          </div>
        ) : null}
        {/* Quick-prompts — focus-triggered (not permanent). On idle blur,
          the chips fade out and the composer card collapses to ~88px. */}
        {showQuickPrompts ? (
          <div className="-mt-1 motion-safe:fade-in motion-safe:slide-in-from-bottom-1 mb-2 flex flex-wrap gap-1.5 motion-safe:animate-in motion-safe:duration-150">
            {quickPrompts.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setValue(s.prompt)}
                className="hover:-translate-y-px rounded-full border border-white/70 bg-card-glass-soft px-3 py-[5px] font-medium text-[11px] text-ink-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_2px_rgba(0,0,0,0.04)] transition-all hover:text-ink-1 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_4px_12px_-4px_rgba(0,0,0,0.08)]"
              >
                {s.label}
              </button>
            ))}
          </div>
        ) : null}

        {/* Slash-command palette — floats above the card while `/` is being typed. */}
        {slashOpen ? (
          <div className="mb-1.5">
            <Composer.SlashCommandMenu
              commands={slashCandidates}
              onSelect={runSlashCommand}
              onDismiss={() => setValue("")}
            />
          </div>
        ) : null}

        {/*
         * Mention autocomplete — handled inline by ``Composer.RichEditor``
         * (Tiptap suggestion plugin + tippy.js popup). No overlay needed
         * at the route level. See ``RichTextEditor.tsx`` for the popup
         * lifecycle (onStart/onUpdate/onKeyDown/onExit).
         */}

        {/* CAD analysis modal — opened from the Tools menu "Analisi CAD" row.
          Resolves a DXF-world rectangle that rides the next message as
          ``spatial_filter``. Portaled (Radix Dialog), so its position in the
          tree doesn't matter. */}
        <Composer.CadAnalysisModal
          open={cadModalOpen}
          onOpenChange={setCadModalOpen}
          files={cadFiles}
          filesLoading={dxfFilesLoading}
          onConfirm={setCadSpatialFilter}
          onConfirmFeature={setCadFeatureFilter}
        />

        {/* Jun 17, 2026 — banner that surfaces the explicit > implicit
            precedence applied in ``useSendQuery``. When the user has an
            explicit scope set (folder ambito, sticky docs, Connect
            Space) the open sidebar viewer no longer grounds the agent;
            this hint tells them so before they send. Self-hides when
            there's no override to report. */}
        <ActiveContextOverrideBanner className="mx-1 mb-1.5" />

        <Composer.Root
          ref={formRef}
          className={cn(
            "composer-card",
            // Structured/full Focus tints the card border so the "querying my
            // data" mode is legible at a glance (v1 parity, ando-orange palette).
            config.focus !== "standard" && "border-ando-orange/25",
          )}
          // v1 parity: drop + paste land directly on the composer card so the
          // user doesn't have to find the attach button. ``useChatAttachments``
          // hook dispatches by MIME (images vs generic files).
          onPaste={pasteHandler}
          {...dragHandlers}
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSend) return;
            const text = value;
            const mentions = selectedMentions;
            const spatialFilter = cadSpatialFilter;
            const featureFilter = cadFeatureFilter;
            // Snapshot attachments BEFORE clearing — the optimistic user
            // message in ``useSendQuery`` needs the same refs the
            // ``clearAttachments()`` call below wipes from the composer.
            //
            // ``previewUrl`` is a local-state-only blob URL (Jun 8 2026)
            // and MUST NOT ride on the wire — strip it via destructure
            // so the WS envelope stays the canonical
            // ``{minio_key, filename, mime_type}`` shape the BE expects.
            // ``canSend`` already gates on ``!isUploading`` so any image
            // in the list has a real ``minio_key`` (the optimistic
            // ``pending:`` placeholder is gone by the time the user can
            // click Send).
            const imageAttachments =
              images.length > 0
                ? images.map(({ previewUrl: _previewUrl, ...wire }) => wire)
                : undefined;
            // Build the wire attachments. A .zip is a CONTAINER, not a readable
            // document: drop its (non-resolvable) ``archive_…`` handle and send
            // the REAL member content_ids instead, so the agent receives them
            // via the normal ``fetch_chat_file_refs`` path (capped BE-side).
            // Strip local-only fields (uploading/extracted_*) from the wire.
            const fileAttachments: ChatFileAttachment[] | undefined = (() => {
              if (files.length === 0) return undefined;
              const out: ChatFileAttachment[] = [];
              for (const f of files) {
                const {
                  uploading: _uploading,
                  extracted_count: _extractedCount,
                  extracted_ids: memberIds,
                  ...wire
                } = f;
                if (memberIds && memberIds.length > 0) {
                  for (const id of memberIds) {
                    out.push({
                      content_id: id,
                      filename: wire.filename,
                      mime_type: wire.mime_type,
                      size_bytes: 0,
                      ingestion_status: "completed",
                    });
                  }
                } else {
                  out.push(wire);
                }
              }
              return out.length > 0 ? out : undefined;
            })();
            setValue("");
            setSelectedMentions([]);
            clearAttachments();
            // CAD filter is per-message — drop it after one send.
            // Goes through the focus-binding wrapper so the walk-back
            // (structured → standard when no connector pinning) fires
            // in lock-step with the chip disappearing.
            setCadSpatialFilter(null);
            setCadFeatureFilter(null);
            void submit(text, {
              mentions,
              spatialFilter: spatialFilter ?? undefined,
              featureFilter: featureFilter ?? undefined,
              imageAttachments,
              fileAttachments,
            });
          }}
        >
          {/* Drag-to-attach affordance — overlays the whole card while a file
            is dragged over it. Desktop (Tauri v2) relies on
            ``dragDropEnabled: false`` in tauri.conf.json so the DOM drop
            actually carries the files (see composer parity plan). */}
          <Composer.DropOverlay active={isDragOver} />
          {/* Header (ModeSelector) — June 11, 2026: ALWAYS rendered.
            Previously gated behind ``!isIdle || activeModeId !== "standard"``
            so the idle card stayed at ~88px, but that gate had two side
            effects the user surfaced as a bug:
              (1) on first entry the mode picker was invisible → user had
                  to type something just to discover Ando · Standard;
              (2) ``isIdle`` flips with composer focus, so opening the
                  picker dropdown blurred the editor → ``isIdle`` went
                  true → header un-mounted → click on the menu option
                  hit empty space ("appena clicco mi scompare").
            SOTA peers (ChatGPT model picker, Claude model dropdown,
            Cursor agent badge) keep their persona/mode chip persistent.
            +24px idle height is the right trade for discoverability +
            click stability. */}
          <Composer.Header
            modes={composerModes}
            activeModeId={activeModeId}
            disabled={isModeLocked}
            kbdHint={null}
            onModeChange={handleModeChange}
            onManageModes={() => {
              const studio = useStudioStore.getState();
              studio.setTab("modes");
              studio.open();
            }}
          />
          {/* Jul 13, 2026 — ambient context chip row (Cursor / Dia
              pattern). Surfaces the entity the user has OPEN in the
              workspace (inbox thread / document / calendar event …) so
              "the chat already knows about X" is VISIBLE and REVOCABLE.
              Reuses the active-context store — the chip's ✕ suppresses
              the open entity from the next send. Self-hides when nothing
              is open, when an explicit scope already overrides it (the
              banner above covers that), or once dismissed. */}
          <AmbientContextChips className="px-3 pt-1.5" />
          <Composer.AttachmentStrip
            // Jun 8, 2026 — pass ``previewUrl`` (blob URL created sync at
            // file-pick time by ``useChatAttachments``) so the thumbnail
            // renders instantly instead of the muted "IMG" placeholder.
            // V1 parity: the user wants visual confirmation that the
            // image was picked up before the BE round-trip completes.
            images={images.map((i) => ({
              key: i.minio_key,
              filename: i.filename,
              previewUrl: i.previewUrl,
            }))}
            onRemove={removeAttachment}
          />
          {audioFiles.length > 0 ? (
            <div className="flex flex-wrap gap-2 px-3 pt-2">
              {audioFiles.map((file) => (
                <div key={file.content_id} className="flex w-full max-w-[400px] items-center gap-2">
                  {file.uploading ? <AudioPlayer filename={file.filename} loading /> : <ChatFileAudio contentId={file.content_id} filename={file.filename} />}
                  <button type="button" onClick={() => removeAttachment(file.content_id)} aria-label={i18n._(msg`Rimuovi ${file.filename}`)} className="rounded-lg p-2 text-ink-3 hover:bg-ink-1/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange">
                    <X size={16} aria-hidden />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <Composer.FileChipList files={fileChips} onRemove={removeAttachment} />
          <Composer.ErrorBanner
            message={errorMessage}
            onDismiss={() => setDismissedError(lastError)}
          />
          <Composer.RichEditor
            value={value}
            minHeight={56}
            placeholder={
              isStreaming
                ? i18n._(msg`Ando sta rispondendo… premi Stop per interrompere`)
                : hasBankingHint
                  ? i18n._(msg`Su che banca / conto / scadenza lavoriamo?`)
                  : composerFirstName
                    ? i18n._(msg`Su cosa lavoriamo, ${composerFirstName}?`)
                    : i18n._(msg`Su cosa lavoriamo?`)
            }
            onChange={(plainText, mentions) => {
              setValue(plainText);
              // Mention list mirrors the editor doc on every keystroke —
              // delete a chip → it disappears from the wire envelope, no
              // divergence possible. Shape is identical to ``SendQueryMention``.
              setSelectedMentions(
                mentions.map((m) => ({
                  kind: m.kind,
                  id: m.id,
                  display: m.display,
                })),
              );
            }}
            onSubmit={() => formRef.current?.requestSubmit()}
            onMentionQuery={onMentionQuery}
          />
          {/* ChipBar — May 24 2026: render only when there are active
            chips OR the user is composing AND wants to attach. Avoids
            an empty 32px strip when nothing is selected. The AttachButton
            stays in the Bar.left below so the user can still toggle
            capabilities from the bar without the chip strip. */}
          {scopeChips.length > 0 ? (
            <Composer.ChipBar
              // June 11, 2026 — floating glass-morphism. La strip
              // active-capabilities non si mangia più ~32px verticali
              // del composer card; flotta sopra il bordo top come pill
              // glass-blur (Raycast/Granola/Linear pattern). Idle
              // composer torna alla sua altezza minima.
              floating
              chips={scopeChips}
              // Scope selection is owned by the Ambito popover.
              onChipDismiss={(id: string) => {
                if (id === "web") setConfig({ webSearchMode: false });
                else if (id === "folders") setConfig({ folderFilter: [] });
                else if (id === "documents") setConfig({ scopedDocuments: [] });
                else if (id === "connect") setConfig({ connectSpace: null });
                else if (id === "terra") setConfig({ terraMode: false });
                else if (id === "cad") {
                  setCadSpatialFilter(null);
                  setCadFeatureFilter(null);
                }
              }}
            />
          ) : null}
          {/* AttachButton moved into Bar.left below (see ``attachNode``) so
            that on idle the composer card collapses to just RichEditor +
            single-row Bar (~88px total) without a phantom AttachButton
            row above. Capabilities still toggle via the same popover. */}
          <Composer.Bar
            left={
              <>
                {/* AttachButton — May 25 2026: capability rows removed.
                  Thinking + Web search are now ONLY in ToolsMenu (the
                  Strumenti wrench button next to ``+``). Surfacing the
                  same toggles in two places creates the "which one is
                  authoritative?" confusion — the active-state pill on the
                  Strumenti button is enough to advertise that the
                  capabilities live there. The ``+`` stays as a pure
                  attachment entry-point (image / file / CAD). */}
                <Composer.AttachButton
                  onAttach={(kind: ComposerAttachKind) => {
                    // "Disegno CAD" → attach a technical file to the message
                    // (STEP/IGES/STL, image or PDF). The user then asks in
                    // natural language ("trova pezzi simili a questo, con più
                    // fori") and the agent's ``find_similar_cad_parts`` tool
                    // embeds it live + searches. The file is also archived.
                    if (kind === "cad") {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = ".step,.stp,.iges,.igs,.stl,image/*,application/pdf";
                      input.multiple = true;
                      input.onchange = () => {
                        if (input.files) void addFiles(input.files);
                      };
                      input.click();
                      return;
                    }
                    const accept =
                      kind === "image"
                        ? "image/*"
                        : ".pdf,.docx,.xlsx,.pptx,.csv,.txt,.md,.html,.zip";
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = accept;
                    input.multiple = true;
                    input.onchange = () => {
                      if (input.files) void addFiles(input.files);
                    };
                    input.click();
                  }}
                />
                <Composer.ToolsMenu
                  webSearchMode={config.webSearchMode}
                  webSearchEnabled={access.web_search}
                  onToggleWebSearch={handleToggleWebSearch}
                  cadActive={cadSpatialFilter !== null || cadFeatureFilter !== null}
                  onOpenCad={handleOpenCad}
                  // TERRA β (Jul 2026) — handler passed ONLY for platform
                  // admins; without it ``ToolsMenu`` hides the row
                  // entirely (leaf package stays role-agnostic).
                  terraActive={config.terraMode}
                  onToggleTerra={isTerraAdmin ? handleToggleTerra : undefined}
                  tramaInfrastructure={config.tramaInfrastructure ?? "current"}
                  onChangeTramaInfrastructure={isTerraAdmin ? handleChangeTramaInfrastructure : undefined}
                  // Readiness stays false until the server supplies exact actor/scope admission.
                  tramaLakehouseAvailable={tramaLakehouseConfigured}
                  disabled={isStreaming}
                  // June 11, 2026 — count counts only EXPLICIT user
                  // toggles. ``focus === "structured"`` is auto-derived
                  // from Ambito → Dati selection so it must NOT add to
                  // the Strumenti badge (the Ambito trigger already
                  // surfaces that selection via its own counter). Pre-
                  // fix the badge double-counted, and the user couldn't
                  // tell why "Strumenti (2)" was lit after only flipping
                  // one toggle.
                  activeCount={
                    (config.webSearchMode ? 1 : 0) +
                    (cadSpatialFilter ? 1 : 0) +
                    (cadFeatureFilter ? 1 : 0) +
                    (config.terraMode ? 1 : 0)
                  }
                />
                {/* "Cerca a fondo" pill — May 26, 2026 two-tier collapse.
                  Replaces the old Quick / Standard / Expert segmented
                  control and the standalone "Pensiero approfondito" row
                  inside ``ToolsMenu``. Gated by ``access.expert_mode``
                  (BE-canonical Pro+ flag); we also accept
                  ``access.thinking_mode`` so users on the legacy
                  thinking-only tier keep their Deep affordance during
                  rollover. */}
                {/* June 11, 2026 — cluster divider (input-augmentation
                  ↔ next-message intent). SOTA peers (ChatGPT, Claude)
                  separano visivamente gli "input augmentation" tools
                  (+, Strumenti) dai flag che cambiano il contratto
                  della prossima risposta (reasoning depth, scope). 1px
                  hairline, mx-0.5 — segnale presente ma non rumoroso. */}
                <span aria-hidden className="mx-0.5 h-4 w-px shrink-0 bg-hairline/70" />
                <Composer.DeepModePill
                  active={config.mode === "deep"}
                  onToggle={(next) => setConfig({ mode: next ? "deep" : "fast" })}
                  enabled={access.thinking_mode || access.expert_mode}
                  disabled={isStreaming}
                />
                {/* SourceScopePicker — June 2026: unified pill per
                  restringere la retrieval a cartelle / file / dati.
                  Riordinato a fianco di Deep (giugno 11, 2026): sono
                  entrambi flag che cambiano il contratto della prossima
                  risposta (depth + breadth). Voice spostata DOPO il
                  prossimo divider, perché è un input-method
                  alternativo (parli invece di scrivere), non un
                  intent flag. Folders → ``folder_filter`` wire field;
                  files → merged into ``mentions[]`` with
                  ``kind: "document"`` via useSendQuery (no new BE
                  contract). */}
                <Composer.SourceScopePicker
                  // Onboarding tour anchor — the "focus" step spotlights this
                  // "Ambito" pill, where the user picks a structured data
                  // source (gestionale / banca) for structured queries.
                  dataTour="focus"
                  folderItems={scopeFolderItems}
                  selectedFolderIds={config.folderFilter}
                  onChangeFolders={(ids) => setConfig({ folderFilter: [...ids] })}
                  foldersLoading={scopeFoldersQuery.isLoading}
                  untrackedConnectedProviders={untrackedConnectedProviders}
                  selectedDocuments={config.scopedDocuments}
                  onChangeDocuments={(docs) =>
                    setConfig({
                      scopedDocuments: docs.map(
                        (d): ScopedDocument => ({
                          id: d.id,
                          display: d.display,
                          // Preserve provenance so the popover re-open
                          // doesn't lose the badge + path on sticky docs.
                          ...(d.subtitle ? { subtitle: d.subtitle } : {}),
                          ...(d.provider ? { provider: d.provider } : {}),
                          ...(d.folderPath ? { folderPath: d.folderPath } : {}),
                        }),
                      ),
                    })
                  }
                  documentQuery={scopeDocQuery}
                  onDocumentQueryChange={handleScopeDocQueryChange}
                  documentResults={scopeDocumentResults}
                  documentsLoading={scopeDocSearch.isLoading}
                  // June 11, 2026 — structured-data slice promoted into
                  // Ambito (was a separate "Focus · Strutturati" chip the
                  // user had to click to open the connector picker — user
                  // feedback: "poco chiaro per i clienti"). Picking
                  // connectors here auto-engages the structured focus mode
                  // so the BE actually queries them; deselecting them all
                  // walks ``focus`` back to standard so the chip echo
                  // disappears too. TRAMA handles source routing automatically.
                  structuredConnectors={structuredConnectors}
                  structuredSingleSelect={lakehouseScope}
                  selectedStructuredIds={config.selectedConnectionIds}
                  onChangeStructured={(ids) => {
                    const next = [...ids];
                    if (lakehouseScope) {
                      setConfig({ selectedConnectionIds: next });
                      return;
                    }
                    if (next.length > 0 && config.focus === "standard") {
                      setConfig({ selectedConnectionIds: next, focus: "structured" });
                    } else if (next.length === 0 && config.focus === "structured") {
                      setConfig({ selectedConnectionIds: next, focus: "standard" });
                    } else {
                      setConfig({ selectedConnectionIds: next });
                    }
                  }}
                  onOpenIntegrations={() => navigate({ to: "/integrations" })}
                  // W9 — gate the mention-search hook on the popover state
                  // so it stops firing once the user dismisses the picker.
                  onOpenChange={setScopeDocPickerOpen}
                />
                {/* ConnectAreaPicker — June 14, 2026. "Interroga un'area": an
                  EXCLUSIVE scope (own pill, not folded into Ambito which is
                  additive). Picking an area pins ``config.connectSpace`` →
                  every turn answers ONLY from that shared area (read-only); the
                  read-only "Area · …" chip echoes it in the ChipBar above. */}
                <Composer.ConnectAreaPicker
                  areas={connectAreaOptions}
                  selectedId={config.connectSpace?.id ?? null}
                  loading={connectAreasLoading}
                  disabled={isStreaming}
                  onSelect={(area) =>
                    setConfig({
                      connectSpace: area
                        ? {
                            id: area.id,
                            name: area.name,
                            ownerOrgName: area.ownerOrgName ?? null,
                          }
                        : null,
                    })
                  }
                  onManage={() => navigate({ to: "/workspace/connect" })}
                />
              </>
            }
            right={
              <>
                {/* Mic paired with Send on the "commit input" axis — moved here
                  from the left cluster. CharCount is hidden and the in-bar
                  fullscreen button lives in the footer now (the composer
                  auto-grows while typing). */}
                <Composer.VoiceButton
                  recording={audio.recording}
                  durationSec={audio.durationSec}
                  onToggle={handleVoiceToggle}
                  disabled={isStreaming || isUploading}
                  unsupported={
                    typeof window !== "undefined" && typeof MediaRecorder === "undefined"
                  }
                />
                <Composer.SendButton canSend={canSend} streaming={isStreaming} onStop={stopQuery} />
              </>
            }
          />
        </Composer.Root>
        {/* Footer — persistent status row: connected-sources count + Focus
          toggle, ALWAYS visible. Was focus-gated (fade-in on focus-within);
          made permanent so the count is glanceable and clicking ``+`` no
          longer shifts the bar up to reveal it. Slim, borderless, quiet. */}
        <div className="mt-2 flex items-center justify-between px-2 text-[11px] text-ink-3">
          <div className="flex items-center gap-1.5">
            <span aria-hidden className="inline-block h-1 w-1 rounded-full bg-success" />
            <span>{connectorsLabel}</span>
          </div>
          <div className="flex items-center gap-0.5">
            {/* Fullscreen composer (⌘⇧E) — next to Focus chat: both are
              "expand" utilities. Distinct icon (``Maximize`` four-corner) vs
              Focus chat's diagonal ``Maximize2``. Hidden once fullscreen. */}
            {!expandedComposer ? (
              <button
                type="button"
                onClick={() => setExpandedComposer(true)}
                aria-label={i18n._(msg`Espandi composer (⌘⇧E)`)}
                title={i18n._(msg`Espandi composer · ⌘⇧E`)}
                className={cn(
                  "inline-flex items-center rounded-md bg-transparent px-1.5 py-0.5 transition-colors",
                  "text-ink-3 hover:text-ink-1",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
                )}
              >
                <Maximize size={11} strokeWidth={1.75} aria-hidden />
              </button>
            ) : null}
            <button
              type="button"
              onClick={toggleFocus}
              aria-pressed={isFocusMode}
              aria-label={
                isFocusMode ? i18n._(msg`Esci da Focus chat`) : i18n._(msg`Apri Focus chat`)
              }
              title={i18n._(msg`Focus chat`)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md bg-transparent px-1.5 py-0.5 transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
                isFocusMode
                  ? "text-ando-orange hover:text-ando-orange/80"
                  : "text-ink-3 hover:text-ink-1",
              )}
            >
              {isFocusMode ? (
                <Minimize2 size={11} strokeWidth={1.75} aria-hidden />
              ) : (
                <Maximize2 size={11} strokeWidth={1.75} aria-hidden />
              )}
              <span>{isFocusMode ? <Trans>Esci da Focus</Trans> : <Trans>Focus chat</Trans>}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );

  if (expandedComposer) {
    return createPortal(
      <div ref={composerRef} className={wrapperClassName}>
        {composerBody}
      </div>,
      document.body,
    );
  }

  return (
    <div ref={composerRef} className={wrapperClassName}>
      {composerBody}
    </div>
  );
}
